import { Injectable, OnDestroy } from "@angular/core";
import { CoreLogService } from "../../core/services/log.service";
import { CoreCOMService } from "../../core/services/com.service";
import { StorageHive, LocalKeys } from "../../core/services/valueStorage.service";
import { IObjectObserver } from "../../core/services/IObjectObserver";
import { ProductObjectObserverData, SOTObjectObserverData } from "../../core/services/objectObserver.service";
import { ObjectObserverFactory } from "../../core/services/objectObserverFactory";
import { CircularInjectionHelper } from "../../core/services/circularInjectionHelper";
import { interval, Observable, Subject, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { SOTWebSocketService } from "./sotWebSocket.service";
import { SOTStyleService } from "./style.service";
import { CouponRedeemResult, ISOTAccessabilty, ISOTCouponValidation, ISOTService, ISOTVoucher, ISOTVoucherBonusAction, ISOTVoucherBonusActionBenefit, SOTDialogResult, VoucherActionType } from "./ISOTService";
import { Router } from "@angular/router";
import * as Models from "../models";
import { ContentType, orderTypes, SortDirection } from "../../core/shared/contentTypes";
import { ArticleAvailabilityDataModel, MenuSettings_SortType, ProductDataModel4, ScheduledForRequest, WebshopSettingsDataModel2, WebshopSettingsDMKey, RecipeLinkingDataModel, ArticleTagDataModel, BonuspointsAction, BonuspointsActionBenefit, BonusactionDataModel, BonusactionBenefitDataModel } from "../../core/shared/models";
import * as moment from "moment";
import * as _ from "lodash";
import Utils from "../../core/shared/utils";
import { ProductDetailsHelperService } from "../../core/services/product_details_helper.service";
import { InternationalisationService } from "../../core/forked/angular-internationalisation";
import { SOTDialogService } from "./dialog.service";
import { MatDialogRef } from "@angular/material/dialog";
import { StorageCleaner } from "../../core/shared/storageCleaner";
import { PagingHelper } from "../../core/shared/pagingHelper";
import { SortOption } from "../../core/shared/models/sort.model";
import { CoreLanguageService } from "../../core/services/language.service";
import { ISOTProductViewModel } from "../models/product.facade";
import { DomSanitizer } from "@angular/platform-browser";
import { WebVoucherService } from "../../core/services/web_voucher.service";
import { CouponNumberPadDialogConfig, NumberPadDialogComponentConfig, ProductSubDialogConfig } from "../dialogs";
import { CoreLanguageManagerService } from "../../core/services/languageManager.service";
import { env } from "../../../environments/environment";
import { EventmanagerService, IEvent } from "../../core/services/eventmanager.service";
import { HYUAR_REDEMPTION_CODE_PREFIX } from "../../core/shared/enums/coupon.enums";

/** The Duration between getting new products data in ms */
const PRODUCT_UPDATERATE = 10 * 60 * 1000; // 10 min

/** The Duration between getting new availibility data in ms */
const ARTICLEAVAILIBILITY_UPDATERATE = 1 * 60 * 1000; // 1 min

/** Maximale speicher größe des buffers für fehler logs */
const MAX_ERROR_BUFFER_LENGTH = 200;

/**
 * Enum um den status der kommunikation abzubilden
 * der der Client und Server sich momentan befinden
 */
enum SOTComunicationStatus {
  /** hat momentan kein aktieven status bei der kommunikation */
  free = 0,
  /** wartet momentan auf die antwort der zahlung */
  wait_payment = 1,
  /** wartet, nach erfolgter zahlung, auf den bon reset */
  wait_reset = 2,
  /** ist momentan im zahlungsvorgang, muss diesen abschließen */
  in_payment = 3,
  /** wartet, auf scannen der Vorgangskarte */
  wait_scan_pager = 4,
  /** wartet, auf scannen der Pagernummer */
  wait_scan_card = 5,
  /** wartet, auf scannen der Couponnummer */
  wait_scan_coupon = 6,
  /** wartet, auf scannen des Gutscheins */
  wait_scan_voucher = 7,
  /** waits for the POS to validate a single-use coupon */
  wait_coupon_validation = 8,
  // ab 999 sind Fehler Codes die den ursrpünglichen code behalten
  /** drucker ist momentan nicht verfügbar, SOT wartet auf behebnung des Problems */
  printer_error = 1000,
}

/** definirte status die das sot hat während der initialisierungs phase */
export enum InitStatus {
  none = "[none]",
  wait_for_init = "Warte auf die Initialisierung...",
  start_init = "Starte die Initialisierung...",
  sync_data_sot = "Synchronisiere SOT-Daten vom Hypersoft Portal...",
  sync_data_products = "Synchronisiere Artikelstamm-Daten vom Hypersoft Portal...",
  prepare_data = "Verarbeitung der Daten...",
  open_connection = "Verbindung zum Hypersoft POS wird hergestellt...",
  error = "Fehler bei der Initialisierung",
  done = "Starte SOT Oberfläche"
}

/** Webgutschein */
class SOTVoucher implements ISOTVoucher {
  private _Code: string = "";
  public get Code(): string {
    return this._Code;
  }
  private _Amount: number = 0;
  public get Amount(): number {
    return this._Amount;
  }
  private _InviteAmount: number = 0;
  public get InviteAmount(): number {
    return this._InviteAmount;
  }
  private _Discount: number = 0;
  public get Discount(): number {
    return this._Discount;
  }
  private _UseBonussystem: boolean = false;
  public get UseBonussystem(): boolean {
    return this._UseBonussystem;
  }
  private _LastPointAction: string = "";
  public get LastPointAction(): string {
    return this._LastPointAction;
  }
  private _BonusActions: SOTVoucherBonusAction[] = [];
  public get BonusActions(): ReadonlyArray<SOTVoucherBonusAction> {
    return this._BonusActions;
  }

  /** Aus dem vom POS übermitelten Daten wird ein SOT-Webgutschein geparst.
   * Achtung: Beim Parsen kann es zu fehlern kommen.
   * @param voucherScanData Aufbau: "Gutschein(|(Customerinfo)|Bonusinfo)" Beispiel "001;12345;0000;500;0"
   * @param coreLogService Wird genutzt um einen Fehler-Log beim Parsen schreiben zu können
   */
  public constructor(voucherScanData: string) {
    const me = this;

    // Gutschein Gutscheinnummer=001;GutscheinWert;0000;EinladungsWert;Rabatt
    // Customerinfo ?
    // bonusinfo Bonusteilname(0 oder 1)§LastPointAction|Actioninfo1|Actioninfo2
    // Actioninfo Bakt1_Name§Bakt1_ID§Bakt1PunkteNeu~Bakt1PunkteAlt§Bakt1_EndDay§Benefitsinfo
    // Benefitsinfo BenefitName~BenefitName2§BenefitPID~BenefitPID2§LossesID~LossesID2§BenefitPunkte~BenefitPunkte2§BenefitEndDay~BenefitEndDay2
    // Beispiel: "1§01.01.2024 08:00:00|Aktion No1§1§10~10§20240201§Benefit1~Benefit2~Benefit3§11~12~13§22~22~22§5~2~20§20241231~20241231~20241231|Aktion No2§2§120~120§20240601§Benefit1-2§21§22§10§20241231"

    me._Code = voucherScanData.substring(0, 18);
    voucherScanData = voucherScanData.substring(19); // gutscheinnummer + "=" abschneiden
    const customerStartIndex = voucherScanData.indexOf("|");
    let voucherValues: string[];
    if (customerStartIndex === -1) {
      voucherValues = voucherScanData.split(";");
      // keine Bonusinformation
    } else {
      voucherValues = voucherScanData.substring(0, customerStartIndex).split(";");
      // Bonusinformation lesen
      const bonusStartIndex = voucherScanData.indexOf("|", customerStartIndex + 1); // +1 um hinter dem Customer zu suchen
      if (bonusStartIndex > -1) {
        const bonusinfo = voucherScanData.substring(bonusStartIndex + 1); // +1 Start Zeichen '|' übergehen
        const bonuspointsView = WebVoucherService.convertBonusPointStringToBonusPointsView({ version: 1, bonuspoints: bonusinfo });
        if (bonuspointsView.hasData) {
          me._UseBonussystem = bonuspointsView.using;
          me._LastPointAction = bonuspointsView.lastPointAction;
          bonuspointsView.actions.forEach(action => {
            me._BonusActions.push(new SOTVoucherBonusAction(action));
          });
        }
      }
    }
    if (voucherValues[0] !== "001") {
      throw new Error("Bonusinfo is not okay");
    }

    me._Amount = Utils.moneyRound(Number.parseInt(voucherValues[1]) / 100);
    me._InviteAmount = Utils.moneyRound(Number.parseInt(voucherValues[3]) / 100);
    me._Discount = Utils.moneyRound(Number.parseInt(voucherValues[4]) / 100);
  }
}

/** Bonusaction */
class SOTVoucherBonusAction implements ISOTVoucherBonusAction {
  private _action: BonuspointsAction;

  public get ID(): number {
    return this._action.id;
  }
  public get Name(): string {
    return this._action.label_de;
  }
  public get Points(): number {
    return this._action.points;
  }
  private _Benefits: SOTVoucherBonusActionBenefit[] = [];
  public get Benefits(): ReadonlyArray<SOTVoucherBonusActionBenefit> {
    return this._Benefits;
  }

  constructor(action: BonuspointsAction) {
    const me = this;
    me._action = action;

    action.benefits.forEach(benefit => {
      me._Benefits.push(new SOTVoucherBonusActionBenefit(benefit));
    });
  }
}

/** Benefit einer Bonusaction */
class SOTVoucherBonusActionBenefit implements ISOTVoucherBonusActionBenefit {
  private _benefit: BonuspointsActionBenefit;

  public get Name(): string {
    return this._benefit.label_de;
  }
  public get PointsCost(): number {
    return this._benefit.points;
  }

  constructor(benefit: BonuspointsActionBenefit) {
    const me = this;
    me._benefit = benefit;
  }
}

/** Setzt den Barrierefreien Modus */
class SOTAccessabilty implements ISOTAccessabilty {
  private modeOne: boolean = false;
  public get useModeOne(): boolean {
    return this.modeOne;
  }
  public SetModeOne(active: boolean) {
    this.modeOne = active;
  }
  private modeTwo: boolean = false;
  public get useModeTwo(): boolean {
    return this.modeTwo;
  }
  public SetModeTwo(active: boolean) {
    this.modeTwo = active;
  }
}

@Injectable()
export class SOTService implements ISOTService, OnDestroy {
  public shouldOpenProduct: boolean = true;
  public shouldNavigateToMenu: boolean = false;
  public isbfMode: boolean = false;

  private _initalisation_status: InitStatus = InitStatus.none;
  /** status text der beim initialisieren angezeigt wird */
  public get initalisation_status(): string {
    return this._initalisation_status;
  }
  private changeInitalisationStatus(status: InitStatus) {
    this._initalisation_status = status;
    this._evInitalisationStatusChanged.next();
  }
  private _evInitalisationStatusChanged: Subject<void> = new Subject();
  /** während des initialisierungs vorgangs wird es gefeuert */
  public get evInitalisationStatusChanged(): Observable<void> {
    return this._evInitalisationStatusChanged.asObservable();
  }

  private _evProductsChanged: Subject<void> = new Subject();
  /** während des initialisierungs vorgangs wird es gefeuert */
  public get evProductsChanged(): Observable<void> {
    return this._evProductsChanged.asObservable();
  }

  /* #### Communication Status #### */
  /** enthält den communications status mit SOT, kann auch fehler status enthalten */
  private _comunication_status: number = SOTComunicationStatus.free;
  /** fragt den status ab, wenn es kein fehler gibt */
  private hasComunicationStatus(status: SOTComunicationStatus): boolean {
    if (status < 1000) { // error starts with 1000
      return this._comunication_status == status;
    } else {
      this._coreLogService.error("SOTService.hasComunicationStatus", "status is from type error", status);
      return false;
    }
  }
  /** prüft ob ein fehler gesetzt ist
   * @param specific_error man kann ein genauen fehler abfragen
   */
  private hasComunicationError(specific_error: SOTComunicationStatus = 0): boolean {
    if (specific_error < 1000) { // error starts with 1000
      return this._comunication_status > 999;
    } else {
      return Math.floor(this._comunication_status / 1000) * 1000 == specific_error;
    }
  }
  /** setzt ein fehler, ohne den status zu verlieren, wenn kein fehler vorliegt */
  private setComunicationError(error: SOTComunicationStatus): void {
    if (error < 1000) { // error starts with 1000
      this._coreLogService.error("SOTService.setComunicationError", "error is not from type error", error);
      return;
    }
    if (this.hasComunicationError()) {
      this._coreLogService.error("SOTService.setComunicationError", "comunication has already a error", this._comunication_status);
      return;
    }
    this._comunication_status += error;
  }
  /** entfernt den fehler
   * @param specific_error man kann ein genauen fehler entfernen
   */
  private removeComunicationError(specific_error: SOTComunicationStatus = 0): void {
    if (!this.hasComunicationError()) {
      this._coreLogService.error("SOTService.removeComunicationError", "comunication has no error", this._comunication_status);
      return;
    }
    if (specific_error < 1000) { // error starts with 1000
      this._comunication_status = (this._comunication_status % 1000);
    } else {
      if (!this.hasComunicationError(specific_error)) {
        this._coreLogService.error("SOTService.removeComunicationError", "comunication has not the specific_error", specific_error, this._comunication_status);
        return;
      }
      this._comunication_status -= specific_error;
    }
  }
  /** setzt den status, wenn kein fehler vorliegt */
  private setComunicationStatus(status: SOTComunicationStatus): void {
    const me = this;
    if (status > 999) { // error starts with 1000
      me._coreLogService.error("SOTService.setComunicationStatus", "status is from type error", status);
      return;
    }
    if (me.hasComunicationError()) {
      me._coreLogService.error("SOTService.setComunicationStatus", "comunication has already a error", me._comunication_status);
      return;
    }

    // wenn das Webgutschein scannen oder die Coupon-Prüfung abgebrochen wird und der Loader noch angezeigt wurde, muss dieser ausgeblendet werden
    const isLoaderWaitStatus = me._comunication_status == SOTComunicationStatus.wait_scan_voucher
      || me._comunication_status == SOTComunicationStatus.wait_coupon_validation;
    if (isLoaderWaitStatus && me._sotDialogService.is_loader_dialog_shown) {
      me._sotDialogService.hideLoaderDialog();
    }

    me._comunication_status = status;
  }

  /* #### END: Communication Status #### */

  private _location: number;
  /** HypCusNr */
  public get location(): number {
    return this._location;
  }
  private _port: number;
  /** anwender der Menü Konzept Darstellung der Abfrage Preise */
  private _menu_concept: boolean;
  /** Pagernummer länge */
  private _pager_length: number;

  private _payAtTerminal: boolean;
  /** zahlung am ecTerminal ist erlaubt */
  public get payAtTerminal(): boolean {
    return this._payAtTerminal;
  }
  private _payAtPOS: boolean;
  /** zahlung an dam POS ist erlaubt */
  public get payAtPOS(): boolean {
    return this._payAtPOS;
  }
  private _payAtCard: boolean;
  /** zahlung mit Vorgangskarte ist erlaubt */
  public get payAtCard(): boolean {
    return this._payAtCard;
  }

  private _allowVoucherPay: boolean
  /** zahlung mit Webgutschein ist erlaubt */
  public get allowVoucherPay(): boolean {
    return this._allowVoucherPay;
  }

  private _useBonussystem: boolean;
  /** Bonussystem wird eingesetzt und berücksichtigt */
  public get useBonussystem(): boolean {
    return this._useBonussystem;
  }

  private _in_house_sortiment: string;
  /** Sortiment das vom SOT verwendet werden wird um InHouse Artikel anzuzeigen */
  public get useInHouseSortiment(): string {
    return this._in_house_sortiment ? this._in_house_sortiment : this._sotSettings.model.in_house_sortiment;
  }

  private _pickup_sortiment: string;
  /** Sortiment das vom SOT verwendet werden wird um Pickup Artikel anzuzeigen */
  public get usePickupSortiment(): string {
    return this._pickup_sortiment ? this._pickup_sortiment : this._sotSettings.model.pickup_sortiment;
  }

  /** Durch den Kunden überschriebene Preisebene für InHouse beim Start */
  private _in_house_pe: number = -1;
  /** Aktuelle Preisebene für das laufende Event */
  private _in_house_pe_event: number = -1;
  /** Vom SOT zu verwendende Preisebene für InHouse */
  private get in_house_pe(): number {
    let temp_in_house_pe = this._in_house_pe && this._in_house_pe >= 0 ? this._in_house_pe : this._sotSettings.model.in_house_default_pe;
    return this._in_house_pe_event && this._in_house_pe_event >= 0 ? this._in_house_pe_event : temp_in_house_pe;
  }

  private _inHouse_events: IEvent[] = [];
  /** TODO */
  public get inHouse_events(): IEvent[] {
    return this._inHouse_events;
  }

  /** Durch den Kunden überschriebene Preisebene für Pickup beim Start */
  private _pickup_pe: number = -1;
  /** Aktuelle Preisebene für das laufende Event */
  private _pickup_pe_event: number = -1;
  /** Vom SOT zu verwendende Preisebene für Pickup */
  private get pickup_pe(): number {
    let temp_pickup_pe = this._pickup_pe && this._pickup_pe >= 0 ? this._pickup_pe : this._sotSettings.model.pickup_default_pe;
    return this._pickup_pe_event && this._pickup_pe_event >= 0 ? this._pickup_pe_event : temp_pickup_pe;
  }

  private _pickup_events: IEvent[] = [];
  /** TODO */
  public get pickup_events(): IEvent[] {
    return this._pickup_events;
  }

  private _hasInHouseOption: boolean = false;
  /** Es wird am SOT erlaubt das InHouse zu nutzen */
  public get hasInHouseOption(): boolean {
    return this._hasInHouseOption;
  }

  private _hasPickupOption: boolean = false;
  /** Es wird am SOT erlaubt das Pickup zu nutzen */
  public get hasPickupOption(): boolean {
    return this._hasPickupOption;
  }

  /** damit bei den Aufrufen der Funktionen der OrderType nicht immer mitgegeben werden muss */
  private _orderType: orderTypes.pickup | orderTypes.inHouse = orderTypes.inHouse;
  /** hält die den gewünschten payment typ fest */
  private _payType: Models.PaymentType = Models.PaymentType.ecTerminal;

  /** gewünschtes Trinkgeld in Prozent */
  private _tip_percent: 0 | 5 | 10 | 15 = 0;
  public get tipPercentage(): number {
    return this._tip_percent;
  }
  /* hinterlegte Pagernummer */
  private _pager_no: number = 0;
  /* gescannte Vorgangskarte */
  private _card_no: string = "";
  /* Verbrauchter Betrag der Vorgangskarte */
  private _card_amount: number = 0;
  /* Limit der Vorgangskarte */
  private _card_limit: number = 0;
  /** gibt die gerade gescannte Vorgangskarte aus, wenn keine Gescannt ist, dann null */
  public get currentCard(): Models.ProcessCard {
    if (this._card_no != "") {
      return {
        no: this._card_no,
        amount: this._card_amount,
        limit: this._card_limit,
      };
    } else {
      return null;
    }
  }

  // der totale verwendete Betrag der Karte, vorheriger verbrauchter Betrag der Karte plus jetztiger Warenkorb
  public get cardTotalAmount() {
    return this.GetBasket().getTotalSum() + this._card_amount;
  }

  // ob der totale verwendete Betrag der Karte im Kartenlimit.
  public get isCardUnderLimit() {
    return this.cardTotalAmount <= this._card_limit;
  }

  private _order_no: string;
  /** aktuelle bestellnummer, die bei erfolgreicher zahlung durch POS vergeben wurde */
  public get currentOrderNo(): string {
    return this._order_no;
  }

  private _order_open_amount: number;
  /** aktueller offener Betrag, die bei erfolgreicher zahlung offengeblieben sind, und am POS zu begleichen sind */
  public get currentOrderOpenAmount(): number {
    return this._order_open_amount;
  }

  private _order_bonus_points: { label_de: string, points: number }[];
  /** aktuell gesammelte Bonus Punkte, die bei erfolgreicher zahlung gutgeschrieben wurden */
  public get currentOrderBonusPoints(): { label_de: string, points: number }[] {
    return this._order_bonus_points;
  }

  private _voucher: SOTVoucher;
  public get voucher(): ISOTVoucher {
    return this._voucher;
  }

  private _localeChanged: Subject<void> = new Subject();
  /** event wenn die Sprache sich ändert (sollte genutzt werden um die Oberfläche neu zu zeichnen) */
  public get LocaleChanged(): Observable<void> {
    return this._localeChanged.asObservable();
  }

  private _confirmedAge: number = -1;

  /* EVENTS */
  private _evPaymentChanged: Subject<void> = new Subject();
  /** wird ausgelöst wenn nach fehlschlagen einer Zahlungsart zu einer anderen gewechselt wird */
  public get evPaymentChanged(): Observable<void> {
    return this._evPaymentChanged.asObservable();
  }
  private _evPaymentDone: Subject<void> = new Subject();
  /** wird ausgelöst die Zahlung erfolgreich war */
  public get evPaymentDone(): Observable<void> {
    return this._evPaymentDone.asObservable();
  }
  private _evResetAfterPayment: Subject<void> = new Subject();
  /** payment war erfolgreich und der bon wurde genomen, nun ein reset zur startseite möglich */
  public get evResetAfterPayment(): Observable<void> {
    return this._evResetAfterPayment.asObservable();
  }
  private _evScanCardDone: Subject<boolean> = new Subject();
  /** wird ausgelöst der Scan einer Vorgangskarte abgeschlossen wurde */
  public get evScanCardDone(): Observable<boolean> {
    return this._evScanCardDone.asObservable();
  }
  private _evScanPagerDone: Subject<boolean> = new Subject();
  /** wird ausgelöst der Scan eines Pagers abgeschlossen wurde */
  public get evScanPagerDone(): Observable<boolean> {
    return this._evScanPagerDone.asObservable();
  }
  private _evScanCouponDone: Subject<string> = new Subject();
  /** wird ausgelöst der Scan eines Coupons abgeschlossen wurde
   * @returns Coupon Code
   */
  public get evScanCouponDone(): Observable<string> {
    return this._evScanCouponDone.asObservable();
  }
  private _evCouponValidationDone: Subject<ISOTCouponValidation> = new Subject();
  /** single-use code currently being validated by the POS ($19) */
  private _couponValidationCode: string = "";
  /** ends the pending $19 request without a $20, null when no request is pending */
  private _interruptCouponValidation: (() => void) | null = null;
  /** reserved redemptions of the current order, until payment success ($03) or release ($21) */
  private _reservedRedemptionIds: string[] = [];
  private _evScanVoucherDone: Subject<ISOTVoucher> = new Subject();
  /** wird ausgelöst wenn der Scan eines Webgutscheins abgeschlossen wurde
   * @returns Webgutschein
   */
  public get evScanVoucherDone(): Observable<ISOTVoucher> {
    return this._evScanVoucherDone.asObservable();
  }

  private _localeChangedSubscription: Subscription;
  private _errorLogReceivedSubscription: Subscription;
  private _connectionStatusChangedSubscription: Subscription;
  private _articleAvailabilityChangesSubscription: Subscription;
  private _inHouseProductsChangedSubscription: Subscription;
  private _pickupProductsChangedSubscription: Subscription;
  private _updateProductsSubscription: Subscription;
  private _updateArticleAvailabilitySubscription: Subscription;

  /** use only SetProductsChanged for changeing */
  private _productsChanged: boolean = false;
  /** Zeigt ob sich Artikel geändert haben und man ein Abgliech mit dem SOT-Cache braucht */
  public get ProductsChanged(): boolean {
    return this._productsChanged;
  }
  private SetProductsChanged(value: boolean) {
    if (this._productsChanged !== value) {
      this._productsChanged = value;
      this._evProductsChanged.next();
    }
  }

  private _sotSettings: Models.SOTSettingsViewModel;
  private _sotStartpages: Models.SOTStartpageViewModel[];
  private _sotAdvertisements: Models.SOTAdvertisementViewModel[];

  private _sotItemGroups: Models.SOTItemGroupDataModel2[]; // für sot (uneingeschränkt)
  private _sotInHouseItemGroups: Models.SOTItemGroupViewModel[]; // für in house
  private _sotPickupItemGroups: Models.SOTItemGroupViewModel[]; // für pickup
  private _sotInHouseRecommendationRules: Models.SOTRecommendationRulesDataModel[]; // für in house
  private _sotPickupRecommendationRules: Models.SOTRecommendationRulesDataModel[]; // für pickup

  private _sotBonusActions: Models.SOTBonusAction[] = []; // für bonus actions

  /** Es wird der beginn der Bestellung gespeichert, um die Zeitsteuerung der Artikel zu berücksichtigen */
  private _sotOrderStart: moment.Moment;
  /** Zeitpunkt wann die Bestellung gestartet wurde, wird beim ersten zugriff definiert */
  private GetOrderStart(): moment.Moment {
    const me = this;
    if (me._sotOrderStart == null) {
      me._sotOrderStart = moment();
    }
    return me._sotOrderStart;
  }
  private _sotBasket: Models.SOTBasketViewModel;
  private _recipeLinkings: RecipeLinkingDataModel[];
  /** cache um die durch Rezepturverkettung versteckte Artikel nicht neu ermitteln zu müssen */
  private _recipeLinkingsHideProductIDs: number[];

  private _sotInHouseProducts: Models.SOTProductViewModel[];
  /** enthält die SOTProductViewModel nach product_id für schnellen zugriff */
  private _sotInHouseProductsMap: { [product_id: number]: Models.SOTProductViewModel };
  private _sotPickupProducts: Models.SOTProductViewModel[];
  /** enthält die SOTProductViewModel nach product_id für schnellen zugriff */
  private _sotPickupProductsMap: { [product_id: number]: Models.SOTProductViewModel };

  private _sotProductTags: Models.SOTProductTagViewModel[];
  /** Artikel TAGs zum Filtern der Artikel am SOT nach product_tag_id für schnellen zugriff */
  private _sotProductTagsMap: { [product_tag_id: number]: Models.SOTProductTagViewModel }
  /** ProductTagIds die fehlen und bereits gemeldet wurden als Fehler */
  private _sotMissingProductTagIds: number[] = [];

  /** hält den Product observer für inHouse (eventuell auch für pickup).
   * ACHTUNG kann null sein wenn inhouse nicht am SOT angeboten wird.
   */
  private _inHouseProductObsServ: IObjectObserver<ProductDataModel4>;
  /** hält den Product observer für pickup, wenn anderes Sortiment als inHouse.
   * ACHTUNG ist null, wenn gleich Sortiment inHouse ist, dann _inHouseProductObsServ nutzen.
   */
  private _pickupProductObsServ: IObjectObserver<ProductDataModel4> = null;
  private _articleAvailabilityObsServ: IObjectObserver<ArticleAvailabilityDataModel>;
  /** halten die daten die momentan während der Bestellung genutzt werden */
  private _articleAvailabilityCountMap: { [productid: number]: number };
  private _articleAvailabilityBlacklistMap: { [productid: number]: boolean };
  /** "new" halten die neuen Daten die in der zwischen zeit gekommen sind */
  private _newArticleAvailabilityCountMap: { [productid: number]: number };
  private _newArticleAvailabilityBlacklistMap: { [productid: number]: boolean };

  /** referenz auf den error dialog */
  private _modalDialogRef: MatDialogRef<any, any>;
  /** referenz auf den aktuel offenen dialog */
  private _openDialogRef: MatDialogRef<any, any>;
  /** referenz auf den bist du noch da dialog */
  private _stillthereDialogRef: MatDialogRef<any, any>;
  private _accessabiltyDialogRef: MatDialogRef<any, any>;
  public hasSkippedOrdertypePage: boolean = false;

  constructor(private _sotWebSocketService: SOTWebSocketService,
    private _sotStyleService: SOTStyleService, // SOTStyleService ist hier um den Constructor, durch den Injector aufzurufen
    private _sotDialogService: SOTDialogService,
    private _internationalisationService: InternationalisationService,
    private _router: Router,
    private _coreCOMService: CoreCOMService,
    private _circularInjectionHelper: CircularInjectionHelper,
    private _domSanitizer: DomSanitizer,
    private _coreLogService: CoreLogService,
    private _coreLanguageService: CoreLanguageService,
    private _coreLanguageManagerService: CoreLanguageManagerService,
    private _eventManagerService: EventmanagerService) {
    console.debug("--- SOTService constructor")
    const me = this;
    me._localeChangedSubscription = me._internationalisationService.localeChanged.subscribe(() => me._localeChanged.next());
    me._errorLogReceivedSubscription = me._coreLogService.errorLogReceived.subscribe((msg) => me.sendErrorLogToSOTServer(msg));
    me._initalisation_status = InitStatus.wait_for_init;
    me._modalDialogRef = null;
    me._openDialogRef = null;
    // für Zeitsteuerung werden WebshopSettings benötigt
    me.InitWebshopSettingsObsServ();
    // ! Achtung, dies ist asynchron und wird hier aufgerufen, um folgend synchrone genutzt werden zu können
    me.getWebshopSettingsDataModel();
  }

  /** cleanup on destry */
  public ngOnDestroy(): void {
    const me = this;
    console.debug("--- SOTService ngOnDestroy")
    if (me._localeChangedSubscription) {
      me._localeChangedSubscription.unsubscribe();
    }
    if (me._errorLogReceivedSubscription) {
      me._errorLogReceivedSubscription.unsubscribe();
    }
    if (me._connectionStatusChangedSubscription) {
      me._connectionStatusChangedSubscription.unsubscribe();
    }
    if (me._articleAvailabilityChangesSubscription) {
      me._articleAvailabilityChangesSubscription.unsubscribe();
    }
    if (me._inHouseProductsChangedSubscription) {
      me._inHouseProductsChangedSubscription.unsubscribe();
    }
    if (me._pickupProductsChangedSubscription) {
      me._pickupProductsChangedSubscription.unsubscribe();
    }
    if (me._updateProductsSubscription) {
      me._updateProductsSubscription.unsubscribe();
    }
    if (me._updateArticleAvailabilitySubscription) {
      me._updateArticleAvailabilitySubscription.unsubscribe();
    }
    // stoppen und damit das entbinden des inactive dialog timers
    me.inactive_dlg_stop();
  }

  /** momentan ausgewählte sprache */
  public GetCurrentLanguage(): string {
    return this._internationalisationService.getCurrentLocale();
  }

  /**checks whether there is text for the service call button */
  public hasServiceCallLabel(serviceCallLabel: string): boolean {
    return this._coreLanguageService.value(serviceCallLabel).length > 0 ? true : false;
  }

  /** setzt für den Startscreen die sprache zurück */
  public SetDefaultLanguage(): void { // todo SetDefaultLanguage muss async werden
    const default_language = this._sotSettings.model.default_language;
    if (this._internationalisationService.getCurrentLocale() != default_language) {
      this.ChangeLanguage(default_language);
    }
  }

  /** versucht die sprache zu ändern, wenn erfolgreich kommt true, sonst false zurück */
  public ChangeLanguage(language: string): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve) => {
      const old_language = me._internationalisationService.getCurrentLocale();
      if (old_language != language) {
        // todo show global loader
        me._coreLanguageManagerService.LoadLanguagesAsync(me.location, language).then(() => {
          //Locale muss hier verändert werden damit das Event zur richtigen Zeit ausgelöst wird
          me._internationalisationService.setCurrentLocale(language);
          // todo hide global loader
          resolve(old_language != me._internationalisationService.getCurrentLocale());
        }).catch((error) => {
          // todo hide global loader
          me._coreLogService.error("SOTService.ChangeLanguage", "LoadLanguagesAsync catch", error);
          resolve(old_language != me._internationalisationService.getCurrentLocale());
        });
      } else {
        resolve(old_language != me._internationalisationService.getCurrentLocale());
      }
    });
  }

  /** übersetzt ein langkey in die gewünschte sprache */
  public TranslateKeyToLanguage(language: string, langkey: string): string {
    return this._internationalisationService.getLocaleValue(language, langkey);
  }

  /** wird der pfand schon im artikel preis angezeigt */
  public get deposit_view(): boolean {
    return this._sotSettings.deposit_view;
  }

  /** format currency */
  public formatCurrency(value: number, hasSymbol: boolean = true, useGrouping: boolean = true): string {
    // todo formatirung muss in der sprache eingestellt werden (also de-DE, en-GB usw.)
    return Utils.currencyFormatter(value, this._sotSettings.currencySymbol, hasSymbol, useGrouping);
  }

  /** Logservice, damit andere es nicht initiieren müssen */
  public get coreLogService(): CoreLogService {
    return this._coreLogService;
  }
  /** Barrierfreier Modus mit dem Modus 1 für 50% der Bildschirmgröße */

  public useModeOne(): boolean {
    return this._SOTAccessabilty.useModeOne;
  }

  public setUseModeOne(value: boolean): void {
    this._SOTAccessabilty.SetModeOne(value);
  }

  public useModeTwo(): boolean {
    return this._SOTAccessabilty.useModeTwo;
  }

  public setUseModeTwo(value: boolean): void {
    this._SOTAccessabilty.SetModeTwo(value);
  }

  private _resetUseModeOne(): void {
    this._SOTAccessabilty.SetModeOne(false);
  }
  private _resetUseModeTwo(): void {
    const colors = Object.keys(this.initialColors);
    colors.forEach(color => {
      document.documentElement.style.setProperty(color.replace('--icolor', '--color'), this.initialColors[color]);
    });

    const fontSizes = Object.keys(this.initialFontSizes);
    fontSizes.forEach(fontSize => {
      document.documentElement.style.setProperty(fontSize, this.initialFontSizes[fontSize]);
    });

    // Reset the font families to the initial values
    const fontFamilies = Object.keys(this.initialFontFamilies);
    fontFamilies.forEach(font => {
      document.documentElement.style.setProperty(font, this.initialFontFamilies[font]);
    });

    this._SOTAccessabilty.SetModeTwo(false);
  }
  private _resetAccessability(): void {
    this._resetUseModeOne();
    this._resetUseModeTwo();
  }
  /** initialisiert das SOT
   * @param location Location des SOT
   * @param port Port zum localen WebSocket des SOT
   * @param payAtTerminal ermöglicht das Zahlen am EC-Terminal
   * @param payAtPOS ermöglicht das Zahlen am POS
   * @param payAtCard ermöglicht das Zahlen mit Vorgangskarte
   * @param allowVoucherPay ermöglicht das Zahlen mit Webgutschein
   * @param useBonussystem wenn allowVoucherPay false ist, dann ist noch das Sammeln von Bonuspunkten erlaubt
   * @param reset bereinigt den Speichet der Location
   * @param menu_concept stellt die Adfrage Preise als Menü Preise dar
   * @param pager_length bestimmt die Länge der Pagernummer
   * @param in_house_sortiment ermöglich das überschreiben vom in_house_sortiment (ist es leer wird es nicht überschrieben)
   * @param pickup_sortiment ermöglich das überschreiben vom pickup_sortiment (ist es leer wird es nicht überschrieben)
   * @param in_house_pe ermöglich das überschreiben vom in_house_pe (ist es leer wird es nicht überschrieben)
   * @param pickup_pe ermöglich das überschreiben vom pickup_pe (ist es leer wird es nicht überschrieben)
   */
  public async init(location: number, port: number, payAtTerminal: boolean, payAtPOS: boolean, payAtCard: boolean, allowVoucherPay: boolean, useBonussystem: boolean, reset: boolean, menu_concept: boolean, pager_length: number, in_house_sortiment: string, pickup_sortiment: string, in_house_pe: number, pickup_pe: number): Promise<void> {
    const me = this;
    me.changeInitalisationStatus(InitStatus.start_init);
    me._location = location;
    me._port = port;

    me._menu_concept = menu_concept;
    me._pager_length = pager_length;

    me._in_house_sortiment = in_house_sortiment;
    me._pickup_sortiment = pickup_sortiment;

    // TODO vom Kundeneingestellte preisebene muss geprüft werden
    if (in_house_pe && in_house_pe >= 0)
      me._in_house_pe = in_house_pe;
    if (pickup_pe && pickup_pe >= 0)
      me._pickup_pe = pickup_pe;

    me._payAtCard = payAtCard;
    if (me._payAtCard) {
      // wenn payAtCard dan darf nicht mit EC oder am POS gezahlt werden
      me._payAtTerminal = false;
      me._payAtPOS = false;
      me._allowVoucherPay = false;
      me._useBonussystem = false;
      if (payAtTerminal || payAtPOS || allowVoucherPay || useBonussystem) {
        me._coreLogService.error("AppComponent.init", "payAtCard is set and (payAtTerminal || payAtPOS || allowVoucherPay || useBonussystem) is set too");
      }
    } else {
      me._payAtTerminal = payAtTerminal;
      me._payAtPOS = payAtPOS;
      me._allowVoucherPay = allowVoucherPay;
      me._useBonussystem = useBonussystem;
    }
    me._order_no = ""; // set empty, for next payment
    me._order_open_amount = 0; // set empty, for next payment
    me._redeemBenefitStateFailed = false; // set false, for next payment
    me._order_bonus_points = []; // set empty, for next payment

    me._voucher = null;
    me._voucherSelectedForPay = false;

    if (me.location == null) {
      me._coreLogService.error("AppComponent.init", "location is not set");
      me._router.navigate(["/sot/error"]); // todo show details on error page
      return; // break here
    }
    if (me._port != null) // keeps default if not set
      me._sotWebSocketService.port = me._port;

    const old_location: number = await me._coreCOMService.environment.valueStorage.get(StorageHive.Local, LocalKeys.sotLocation);

    if (old_location && (old_location != me.location || reset)) {
      await me.cleanupLocationData(old_location);
    }

    await me._coreCOMService.environment.valueStorage.set(StorageHive.Local, LocalKeys.sotLocation, me.location);
    // OrderStart wird resetet, damit es beim ersten Zugriff bestimmt wird
    me._sotOrderStart = null;
    me._sotBasket = new Models.SOTBasketViewModel(me);

    try {
      await me.LoadServerData();
    } catch (error) {
      me.changeInitalisationStatus(InitStatus.error);
      me._router.navigate(["/sot/error"]);
      me._coreLogService.error("AppComponent.init", "LoadServerData try catch", error);
      return; // break here
    }

    me.changeInitalisationStatus(InitStatus.open_connection);
    me.BindSOTConnection();

    if (me._port != null) {
      await me.WaitOpenSOTConnection();
    } else {
      me._coreLogService.error("AppComponent.init", "Start without WebSocket");
    }

    me.changeInitalisationStatus(InitStatus.done);
    me.GoToStartscreen();
  }

  /** returnt funktion zum sortieren der artikel nach den einstellungen */
  private get product_sort(): ((l: Models.SOTProductViewModel, r: Models.SOTProductViewModel) => number) {
    const me = this;
    const pagingHelper = new PagingHelper();
    const sortOptions: SortOption[] = [];
    if (me._sotSettings.model.sort_type === MenuSettings_SortType.FreeItemnumber) {
      sortOptions.push(pagingHelper.createSortOption("free_item_nr", SortDirection.ASC));
    } else if (me._sotSettings.model.sort_type === MenuSettings_SortType.Alphabetic) {
      sortOptions.push(pagingHelper.createSortOption("label", SortDirection.ASC));
    } /* else if MenuSettings_SortType.ItemNumber only sorts by artikel_nr */
    // Always sort by artikel_nr last
    sortOptions.push(pagingHelper.createSortOption("artikel_nr", SortDirection.ASC));
    return pagingHelper.fieldSorter(sortOptions);
  }

  /** Lädt alle notwendigen Daten aus dem internet herunter */
  private LoadServerData(): Promise<void> {
    const me = this;
    return new Promise<void>(async (resolve, reject) => {
      try {
        me.changeInitalisationStatus(InitStatus.sync_data_sot);
        let awaitList = new Array<Promise<void>>();
        const sot_data = new SOTObjectObserverData(me.location);
        // produkte seperat laden (um selbe bilder nicht doppelt und Producte ohne Bilder nicht zu laden)
        const sotSettingsObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<Models.SOTSettingsDataModel>(ContentType.SOTSettings, me._circularInjectionHelper, sot_data);
        awaitList.push(sotSettingsObsServ.RefreshObjectsFromServer());
        const recipeLinkingObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<RecipeLinkingDataModel>(ContentType.RecipeLinking, me._circularInjectionHelper, sot_data);
        awaitList.push(recipeLinkingObsServ.RefreshObjectsFromServer());
        const sotStartpageObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<Models.SOTStartpageDataModel>(ContentType.SOTStartpage, me._circularInjectionHelper, sot_data);
        awaitList.push(sotStartpageObsServ.RefreshObjectsFromServer());
        const sotAdvertisementObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<Models.SOTAdvertisementDataModel>(ContentType.SOTAdvertisement, me._circularInjectionHelper, sot_data);
        awaitList.push(sotAdvertisementObsServ.RefreshObjectsFromServer());
        const sotItemGroupObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<Models.SOTItemGroupDataModel2>(ContentType.SOTItemGroup, me._circularInjectionHelper, sot_data);
        awaitList.push(sotItemGroupObsServ.RefreshObjectsFromServer());
        const sotRecommendationRulesObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<Models.SOTRecommendationRulesDataModel>(ContentType.SOTRecommendationRules, me._circularInjectionHelper, sot_data);
        awaitList.push(sotRecommendationRulesObsServ.RefreshObjectsFromServer());
        // keine Bindung an ObserveChanges, da es nur einmal geholt wird, wenn das SOT Getartet wurde

        await Promise.all(awaitList);
        me.changeInitalisationStatus(InitStatus.prepare_data);

        me._sotSettings = new Models.SOTSettingsViewModel(await sotSettingsObsServ.GetObject(0)); // 0 ID for Settings
        if (!me._sotSettings) {
          reject("no settings");
          return; // break here
        } else if (!me._sotSettings.allow_in_house && !me._sotSettings.allow_pickup) {
          reject("no ordertype (in_house/takeaway)");
          return; // break here
        }
        sotSettingsObsServ.Release(); // free ram

        // Lade die möglichen Sprachen
        for (let i = 0; i < me._sotSettings.languages.length; i++) {
          const language = me._sotSettings.languages[i];
          await me._coreLanguageManagerService.LoadLanguagesAsync(me.location, language);
          me._internationalisationService.setCurrentLocale(language);
        }
        me.SetDefaultLanguage();

        me._sotStartpages = (await sotStartpageObsServ.GetObjectsByFilter((startpage) => startpage.active && !startpage.deleted))
          .sort((l, r) => l.sort_no - r.sort_no)
          .map((startpage) => new Models.SOTStartpageViewModel(startpage));
        if (me._sotStartpages.length == 0) {
          reject("no startpage");
          return; // break here
        }
        sotStartpageObsServ.Release(); // free ram
        me._sotAdvertisements = (await sotAdvertisementObsServ.GetObjectsByFilter((advertisement) => advertisement.active && !advertisement.deleted))
          .sort((l, r) => l.sort_no - r.sort_no)
          .map((advertisement) => new Models.SOTAdvertisementViewModel(advertisement));
        sotAdvertisementObsServ.Release(); // free ram
        me._sotItemGroups = (await sotItemGroupObsServ.GetObjectsByFilter((item_group) => item_group.active && !item_group.deleted && item_group.article_group_ids.length > 0))
          .sort((l, r) => l.sort_no - r.sort_no);
        if (me._sotItemGroups.length == 0) {
          reject("no item group");
          return; // break here
        }
        sotItemGroupObsServ.Release(); // free ram

        me._recipeLinkings = await recipeLinkingObsServ.GetObjectsByFilter((link) => !link.deleted && link.use_sot);
        recipeLinkingObsServ.Release(); // free ram
        me._recipeLinkingsHideProductIDs = me._recipeLinkings.reduce((prev, link) => {
          prev.push(...link.products.filter((prod) => prod.hide).map((prod) => prod.product_id));
          return prev;
        }, []);

        const sotRecommendationRules = await sotRecommendationRulesObsServ.GetObjectsByFilter((rule) => rule.active && !rule.deleted);
        sotRecommendationRulesObsServ.Release(); // free ram
        me._sotInHouseRecommendationRules = sotRecommendationRules.filter((rule) => rule.use_in_house_service);
        me._sotPickupRecommendationRules = sotRecommendationRules.filter((rule) => rule.use_pickup_service);

        // initiate product observer
        if (me._sotSettings.allow_in_house) {
          const inHouse_product_data = new ProductObjectObserverData(me.location, me.useInHouseSortiment);
          me._inHouseProductObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<ProductDataModel4>(ContentType.sub_Product, me._circularInjectionHelper, inHouse_product_data);
        }
        // pickup kann durch inHouse abgedeckt werden wenn beide das selbe Sortiment nutzen
        const needPickup = (me.usePickupSortiment !== me.useInHouseSortiment || !me._inHouseProductObsServ)
        if (me._sotSettings.allow_pickup && needPickup) {
          const pickup_product_data = new ProductObjectObserverData(me.location, me.usePickupSortiment);
          me._pickupProductObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<ProductDataModel4>(ContentType.sub_Product, me._circularInjectionHelper, pickup_product_data);
        }

        const webshopsettings = await me.getWebshopSettingsDataModel();

        me.changeInitalisationStatus(InitStatus.sync_data_products);
        await me.syncProductDataFromServer();

        me.changeInitalisationStatus(InitStatus.prepare_data);
        await me.syncProductDataToCache();

        // sich auf änderungen der Artikel regestrieren
        if (me._inHouseProductObsServ) {
          me._inHouseProductsChangedSubscription = me._inHouseProductObsServ.ObserveChanges.subscribe(() => {
            me.SetProductsChanged(true);
          });
        }
        if (me._pickupProductObsServ) {
          me._pickupProductsChangedSubscription = me._pickupProductObsServ.ObserveChanges.subscribe(() => {
            me.SetProductsChanged(true);
          });
        }

        // interval benerieren um neuerungen vom Server abzurufen
        me._updateProductsSubscription = interval(PRODUCT_UPDATERATE).subscribe(() => {
          if (me._coreCOMService.environment.networkInformation.isOnline) {
            me.syncProductDataFromServer(); // no need to wait
          }
        });

        // verfügbarkeit
        if (me._sotSettings.model.use_product_availability) {
          me._newArticleAvailabilityCountMap = {};
          me._newArticleAvailabilityBlacklistMap = {};
          me._articleAvailabilityObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<ArticleAvailabilityDataModel>(ContentType.ArticleAvailabililty, me._circularInjectionHelper);
          me._articleAvailabilityChangesSubscription = me._articleAvailabilityObsServ.ObserveChanges.subscribe(() => me.onArticleAvailbilityUpdate());
          await me._articleAvailabilityObsServ.RefreshObjectsFromServer();
          me.onArticleAvailbilityUpdate();
          me.UseNewAvailability(); // wird einmal aufgerufen um die neuen Daten initial zu setzen
          me._updateArticleAvailabilitySubscription = interval(ARTICLEAVAILIBILITY_UPDATERATE).subscribe(() => {
            if (me._coreCOMService.environment.networkInformation.isOnline) {
              me._articleAvailabilityObsServ.RefreshObjectsFromServer();
            }
          });
        }

        // Events Cachen die relevant werden eventPE
        if (webshopsettings.events && webshopsettings.events.length > 0) {
          me._pickup_events = me._eventManagerService.filterEventsByOrderTypeAndLocation(webshopsettings.events, orderTypes.pickup, me.location, true);
          me._inHouse_events = me._eventManagerService.filterEventsByOrderTypeAndLocation(webshopsettings.events, orderTypes.inHouse, me.location, true);
        } //else Events bleiben leer

        if (me._sotSettings.model.allow_benefits_at_sot) {
          me._sotBonusActions = await me.getBonusActions();
        }
        // resolve if the serverdat loaded succesfull
        resolve();
      } catch (error) {
        reject(error);
        me._coreLogService.error("SOTService.LoadServerData", "try catch", error);
      }
    });
  }

  /** ruft die Daten vom Server ab */
  private async syncProductDataFromServer(): Promise<void> {
    const me = this;
    // alle products
    const syncJobs = [];
    if (me._inHouseProductObsServ) {
      syncJobs.push(me._inHouseProductObsServ.RefreshObjectsFromServer());
    }
    if (me._pickupProductObsServ) {
      syncJobs.push(me._pickupProductObsServ.RefreshObjectsFromServer());
    }

    // item groups auch aktualisieren, damit neue Artikel mit neuen article_group_ids angezeigt werden
    const sot_data = new SOTObjectObserverData(me.location);
    const sotItemGroupObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<Models.SOTItemGroupDataModel2>(ContentType.SOTItemGroup, me._circularInjectionHelper, sot_data);
    syncJobs.push(sotItemGroupObsServ.RefreshObjectsFromServer().then(async () => {
      me._sotItemGroups = (await sotItemGroupObsServ.GetObjectsByFilter((item_group) => item_group.active && !item_group.deleted && item_group.article_group_ids.length > 0))
        .sort((l, r) => l.sort_no - r.sort_no);
      sotItemGroupObsServ.Release(); // free ram
    }));

    // recipe linkings auch aktualisieren, damit neue oder geänderte Rezepturverkettungen angezeigt werden
    const recipeLinkingObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<RecipeLinkingDataModel>(ContentType.RecipeLinking, me._circularInjectionHelper, sot_data);
    syncJobs.push(recipeLinkingObsServ.RefreshObjectsFromServer().then(async () => {
      me._recipeLinkings = await recipeLinkingObsServ.GetObjectsByFilter((link) => !link.deleted && link.use_sot);
      me._recipeLinkingsHideProductIDs = me._recipeLinkings.reduce((prev, link) => {
        prev.push(...link.products.filter((prod) => prod.hide).map((prod) => prod.product_id));
        return prev;
      }, []);
      recipeLinkingObsServ.Release(); // free ram
    }));

    // alle product details auf einmal laden
    let sortiments = [];
    if (me._sotSettings.allow_in_house) {
      sortiments.push(me.useInHouseSortiment)
    }
    if (me._sotSettings.allow_pickup) {
      sortiments.push(me.usePickupSortiment)
    }
    syncJobs.push(ProductDetailsHelperService.GetAllProductDetails(me.location, sortiments, me._circularInjectionHelper, me._coreCOMService.environment.valueStorage));

    await Promise.all(syncJobs);
  }

  /** Wenn System läuft und ein evProductsChanged gefeuert wird, kann damit das SOT aktualisiert werden */
  public async refreshProductData(): Promise<void> {
    const me = this;
    if (me.initalisation_status !== InitStatus.done) {
      me._coreLogService.error("SOTService.refreshProductData", "initalisation_status is not done", me.initalisation_status);
    }
    me.changeInitalisationStatus(InitStatus.prepare_data);
    await me.syncProductDataToCache();
    me.changeInitalisationStatus(InitStatus.done);
  }

  /** schreibt die Artikel Daten in den Cache für das SOT */
  private async syncProductDataToCache(): Promise<void> {
    const me = this;
    me.SetProductsChanged(false); // changes werden übernommen

    // working data
    let sotInHouseProducts = [];
    let sotInHouseItemGroups = [];
    let sotInHouseProductsMap = {};
    let sotPickupProducts = [];
    let sotPickupItemGroups = [];
    let sotPickupProductsMap = {};
    let sotProductTags = [];
    let sotProductTagsMap = {};

    // products
    me._lastSortLanguageKey = me.GetCurrentLanguage(); // initial werden alle Artikel nach der actuellen sprache sortiert
    const product_filter = (product: ProductDataModel4) => !product.deleted &&
      Math.abs(product.usingtype) !== 4; // usingtype 4/-4 = Webvoucher
    if (me._sotSettings.allow_in_house) {
      // get aktive products
      const products = await me._inHouseProductObsServ.GetObjectsByFilter(product_filter);

      // load details for products
      for (let index = 0; index < products.length; ++index) {
        const product = products[index];
        // synchrone herunterladen, da asynchrone hängt bei 1000 offenen verbindungen
        const product_details = await ProductDetailsHelperService.GetProductDetails(product, me._circularInjectionHelper, true, false, true /* suppress_change_at_diff weil GetAllProductDetails zuvor aufgerufen wurde */)
        const product_model = new Models.SOTProductViewModel(product, product_details, me.useInHouseSortiment, sotInHouseProductsMap, me);
        sotInHouseProducts.push(product_model);
        sotInHouseProductsMap[product.product_id] = product_model;
      }
      sotInHouseProducts.sort(me.product_sort);

      // item groups
      sotInHouseItemGroups = me._sotItemGroups.map((itemgroup) => new Models.SOTItemGroupViewModel(itemgroup, "in_house", sotInHouseProducts, me._recipeLinkings, me._recipeLinkingsHideProductIDs, me._productTagFilter, me.product_sort));
      sotInHouseItemGroups = sotInHouseItemGroups.filter((itemgroup) => itemgroup.products.length > 0); // keep itemgroups with products
    }

    if (me._sotSettings.allow_pickup) {
      if (me._sotSettings.allow_in_house && me.useInHouseSortiment == me.usePickupSortiment) {
        // in_house and pickup use the equal sortiment
        sotPickupProducts = sotInHouseProducts;
        sotPickupItemGroups = sotInHouseItemGroups;
        sotPickupProductsMap = sotInHouseProductsMap;
      } else {
        // get aktive products
        const products = await me._pickupProductObsServ.GetObjectsByFilter(product_filter);
        me._pickupProductObsServ.Release(); // free ram

        // load details for products
        for (let index = 0; index < products.length; ++index) {
          const product = products[index];
          // synchrone herunterladen, da asynchrone hängt bei 1000 offenen verbindungen
          const product_details = await ProductDetailsHelperService.GetProductDetails(product, me._circularInjectionHelper, true, false, true /* suppress_change_at_diff weil GetAllProductDetails zuvor aufgerufen wurde */)
          const product_model = new Models.SOTProductViewModel(product, product_details, me.usePickupSortiment, sotPickupProductsMap, me);
          sotPickupProducts.push(product_model);
          sotPickupProductsMap[product.product_id] = product_model;
        }
        sotPickupProducts.sort(me.product_sort);

        // item groups
        sotPickupItemGroups = me._sotItemGroups.map((itemgroup) => new Models.SOTItemGroupViewModel(itemgroup, "pickup", sotPickupProducts, me._recipeLinkings, me._recipeLinkingsHideProductIDs, me._productTagFilter, me.product_sort));
        sotPickupItemGroups = sotPickupItemGroups.filter((itemgroup) => itemgroup.products.length > 0); // keep itemgroups with products

      }
    }

    const hasInHouseItemGroups = sotInHouseItemGroups.length > 0;
    const hasPickupItemGroups = sotPickupItemGroups.length > 0;
    me._hasInHouseOption = me._sotSettings.allow_in_house && hasInHouseItemGroups;
    if (me._sotSettings.allow_in_house && !hasInHouseItemGroups) {
      if (!(me._sotSettings.allow_pickup && hasPickupItemGroups)) {
        return Promise.reject("no in house item group"); // break here
      } else {
        me._coreLogService.error("SOTService.syncProductDataToCache", "no in house item group, hide and use pickup");
      }
    }
    me._hasPickupOption = me._sotSettings.allow_pickup && hasPickupItemGroups;
    if (me._sotSettings.allow_pickup && !hasPickupItemGroups) {
      if (!(me._sotSettings.allow_in_house && hasInHouseItemGroups)) {
        return Promise.reject("no pickup item group"); // break here
      } else {
        me._coreLogService.error("SOTService.syncProductDataToCache", "no pickup item group, hide and use in house");
      }
    }

    // aktive Artikel TAGs lesen
    try {
      const articleTagObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<ArticleTagDataModel>(ContentType.ProductTag, me._circularInjectionHelper);
      const articleTags = await articleTagObsServ.GetObjectsByFilter(tag => tag.active && !tag.deleted);
      articleTags.sort((a, b) => a.sort_no - b.sort_no);
      articleTags.forEach(articleTag => {
        const sotProductTag = new Models.SOTProductTagViewModel(articleTag, me._domSanitizer);
        sotProductTags.push(sotProductTag);
        sotProductTagsMap[sotProductTag.id] = sotProductTag;
      });
    } catch (error) {
      me._coreLogService.error("SOTService.syncProductDataToCache", "get product tags catch", me.initalisation_status);
    }

    // anwenden der Änderungen
    me._sotInHouseProducts = sotInHouseProducts;
    me._sotInHouseItemGroups = sotInHouseItemGroups;
    me._sotInHouseProductsMap = sotInHouseProductsMap;
    me._sotPickupProducts = sotPickupProducts;
    me._sotPickupItemGroups = sotPickupItemGroups;
    me._sotPickupProductsMap = sotPickupProductsMap;
    me._sotProductTags = sotProductTags;
    me._sotProductTagsMap = sotProductTagsMap;
  }

  /** bereinigt den speicher in dem es die daten des standorts löscht
   * wird genutzt falls das SOT an unterschiedlichen standorten gestartet wird (im echteinsatzt sollte das nicht vorkommen)
   */
  private async cleanupLocationData(location: number): Promise<void> {
    const me = this;
    // letzter Details aufruf wird im storage gespeicher, er muss zuerst bereinigt werden.
    await me._coreCOMService.environment.valueStorage.remove(StorageHive.Local, LocalKeys.sotAllProductDetailsCall);
    await StorageCleaner.CleanUpDataOfSOT(this._coreCOMService, location);
  }

  /* ##### use reaction block ##### */

  /** interner status, ob die die Activitätsüberwachung läuft */
  private _isUserActionDetectionActive: boolean = false;
  /** millisekunden der möglichen inactivität, für performance um es nur beim start zu berechnen */
  private _userInactiveTimeoutPeriod: number;
  /** referenz auf den  */
  private _userInactiveTimeout: NodeJS.Timer = undefined;

  /** seconds to auto show inactive dialog, if no activity is detected */
  private get inactive_dlg_show_timeout(): number {
    return Math.min(Math.max(this._sotSettings.model.inactive_dlg_show_timeout, 5), 120); // between limit min: 5 - max: 120

  }
  /** seconds to auto close inactive dialog */
  private get inactive_dlg_close_timeout(): number {
    return Math.min(Math.max(this._sotSettings.model.inactive_dlg_close_timeout, 5), 60); // between limit min: 5 - max: 60
  }

  /** startet das warten auf Anwender inactivitätz zum anzeigen des inactive Dialogs */
  private inactive_dlg_start(): void {
    const me = this;
    me._userInactiveTimeoutPeriod = me.inactive_dlg_show_timeout * 1000;
    me._isUserActionDetectionActive = true;
    // initial wird davon ausgegeangen das der user activ ist
    me.inactive_dlg_startTimer();
  }

  /** re-/startet den inactivitäts timer, bei einer action */
  private inactive_dlg_startTimer(): void {
    const me = this;
    clearTimeout(this._userInactiveTimeout);
    me._userInactiveTimeout = setTimeout(() => { me.inactive_dlg_userInactive() }, me._userInactiveTimeoutPeriod);
  }

  /** wird ausgeführt, wenn der Anwender keine activität im vorgegebenen zeitraum durchgeführt hat
   * es stoppt automatisch den timer
   */
  private inactive_dlg_userInactive(): void {
    const me = this;
    me.inactive_dlg_stop();
    if (me.hasComunicationError(SOTComunicationStatus.printer_error)) {
      // momently the printer error dialog is shown, wait for next inactivity
      me.inactive_dlg_start();
    } else {
      me._stillthereDialogRef = me._sotDialogService.openStillThereDialog(me.inactive_dlg_close_timeout, me)
      me._stillthereDialogRef.afterClosed().subscribe((result) => {
        if (result === "yes") {
          me.inactive_dlg_start(); // reset
        } else if (result === "no") {
          me.GoToOrdertype();
        } else /* "timeout" */ {
          me.GoToStartscreen();
        }
        me._stillthereDialogRef = null;
      });
    }
  }
  /** stopps the inactivity detection timer */
  private inactive_dlg_stop(): void {
    const me = this;
    if (me._isUserActionDetectionActive) {
      me._isUserActionDetectionActive = false;
      clearTimeout(me._userInactiveTimeout);
      me._userInactiveTimeout = null;
    }
  }

  /** wird aufgerufen, wenn eine benutzer action regestriert wurde */
  public userActionDetected(closeStillthereDialog: boolean = false): void {
    if (closeStillthereDialog && this._stillthereDialogRef) {
      this._stillthereDialogRef.close("yes");
    } else if (this._isUserActionDetectionActive) {
      this.inactive_dlg_startTimer();
    }
  }

  /* ## END: use reaction block ## */

  /** naviegiert zur startseite und resettet sich für den nächsten einkauf */
  public async GoToStartscreen(): Promise<void> {
    this.GoToStart(false);
  }

  /** navigiert zum ordertype */
  public async GoToOrdertype(): Promise<void> {
    this.GoToStart(true);
  }

  /** interne funktion zum navigieren in den startscreen oder bereits in den ordertype */
  private async GoToStart(to_ordertype: boolean): Promise<void> {
    const me = this;
    me.inactive_dlg_stop();
    // Falls ein activer scan noch stattfindet cancelscanning senden
    if (me.hasComunicationStatus(SOTComunicationStatus.wait_scan_card)
      || me.hasComunicationStatus(SOTComunicationStatus.wait_scan_pager)
      || me.hasComunicationStatus(SOTComunicationStatus.wait_scan_coupon)
      || me.hasComunicationStatus(SOTComunicationStatus.wait_scan_voucher)) {
      me.SOTConnectionSendCancelScanning();
    }
    // Falls eine Coupon-Prüfung noch läuft, das Warten auf die Kasse beenden
    if (me.hasComunicationStatus(SOTComunicationStatus.wait_coupon_validation)) {
      me.SOTConnectionInterruptCouponValidation();
    }
    // sicherheitshalber wird der comunication_status dabei zurückgesetzt, fals nicht free
    if (me._comunication_status !== SOTComunicationStatus.free) {
      me._coreLogService.error("SOTService.GoToStartscreen", "comunication_status is not free", me._comunication_status);
      if (me._comunication_status === SOTComunicationStatus.in_payment) {
        me.SOTConnectionSendCancel(false);
      }
      me._comunication_status = SOTComunicationStatus.free;
    }
    me.CleanUp();
    if (to_ordertype) {
      // umleitung auf die auswahl ordertype oder gleich in das menü
      if (me._sotSettings.skip_ordertype_if_no_choise && (me.hasInHouseOption != me.hasPickupOption)) {
        me.hasSkippedOrdertypePage = true; // Ordertype-Seite wurde übersprungen
        if (me.hasInHouseOption) {
          me.GoToMenu(orderTypes.inHouse);
        } else { // if (me.hasPickupOption)
          me.GoToMenu(orderTypes.pickup);
        }
      } else {
        me.hasSkippedOrdertypePage = false;
        await me._router.navigate(["/sot/ordertype"]);
      }
      me.inactive_dlg_start();
    } else {
      // umleitung auf den splashscreen/startseite
      me.hasSkippedOrdertypePage = false;
      await me._router.navigate(["/sot/splashscreen"]);
      me.SetDefaultLanguage();
      me.ResortProducts();
    }
    me._resetAccessability();
  }

  /** buffert die Daten für die Product Componente (ab Angular 7.2.0 kann es auch mit der naviagation als state übergeben werden) */
  private _product_component_buffer: { product: ISOTProductViewModel | Models.SOTBasketProductEditViewModel, itemGroup: Models.SOTItemGroupViewModel, recipeLink: ISOTProductViewModel, callback: () => void, couponId?: string, couponCode?: string, redemptionId?: string } = null;
  /**
   * stellt die Daten für die Product Componente (ab Angular 7.2.0 kann es auch mit der naviagation als state übergeben werden)
   * ! darf nur von der product componente aufgerufen werden
   */
  public GetAndClearProductComponentBuffer(): { product: ISOTProductViewModel | Models.SOTBasketProductEditViewModel, itemGroup: Models.SOTItemGroupViewModel, recipeLink: ISOTProductViewModel, callback: () => void, couponId?: string, couponCode?: string, redemptionId?: string } {
    const buffer = this._product_component_buffer;
    this._product_component_buffer = null;
    return buffer;
  }

  /** Cardlimit Warnung öffnen*/
  public async OpenCardlimitDialog(callback: () => void = null) {
    const me = this;
    if (me._openDialogRef === null) {
      const config = {
        sot_service: me
      };
      me._openDialogRef = me._sotDialogService.openCardlimitDialog(config);
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null; // free buffer
        if (callback) {
          callback();
        }
      });
      return true;
    } else {
      return false;
    }
  }

  /** Cardlimit Warnung öffnen*/
  public async OpenCardCouldNotBeReadDialog(callback: () => void = null) {
    const me = this;
    if (me._openDialogRef === null) {
      me._openDialogRef = me._sotDialogService.openCardCouldNotBeReadDialog(me);
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null; // free buffer
        if (callback) {
          callback();
        }
      });
      return true;
    } else {
      return false;
    }
  }

  /** Cardlimit Warnung öffnen*/
  public async OpenPagerCouldNotBeReadDialog(callback: () => void = null) {
    const me = this;
    if (me._openDialogRef === null) {
      me._openDialogRef = me._sotDialogService.openPagerCouldNotBeReadDialog(me);
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null; // free buffer
        if (callback) {
          callback();
        }
      });
      return true;
    } else {
      return false;
    }
  }

  /** open coupon warning dialog*/
  public async OpenCouponCouldNotBeReadDialog(callback: (rescan: boolean) => void) {
    const me = this;
    if (me._openDialogRef === null) {
      me._openDialogRef = me._sotDialogService.openCouponCouldNotBeReadDialog(me);
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null; // free buffer
        if (result === "rescan") {
          callback(true);
        } else if (result === "continue") {
          callback(false);
        }
      });
      return true;
    } else {
      return false;
    }
  }

  /** open coupon article not available dialog*/
  public async openCouponArticleNotAvailable(callback: (rescan: boolean) => void) {
    const me = this;
    if (me._openDialogRef === null) {
      me._openDialogRef = me._sotDialogService.openCouponArticleNotAvailable(me);
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null; // free buffer
        if (result === "rescan") {
          callback(true);
        } else if (result === "continue") {
          callback(false);
        }
      });
      return true;
    } else {
      return false;
    }
  }

  /** Cardlimit Warnung öffnen*/
  public async OpenTippingDialog(callback: () => void = null) {
    const me = this;
    if (me._openDialogRef === null) {
      me._openDialogRef = me._sotDialogService.openTippingDialog(me._sotBasket.getTotalSum(), me);
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null; // free buffer
        me._tip_percent = result;
        if (callback) {
          callback();
        }
      });
      return true;
    } else {
      return false;
    }
  }

  /** Artikel zum Bearbeiten öffnen */
  public async OpenProduct(product: ISOTProductViewModel, itemGroup: Models.SOTItemGroupViewModel, recipeLink: ISOTProductViewModel, callback: () => void): Promise<void> {
    const me = this;
    me._product_component_buffer = {
      product: product,
      itemGroup: itemGroup,
      recipeLink: recipeLink,
      callback: callback,
    }
    if (product.is_recipe_linking) {
      await me._router.navigate(["/sot/recipelink"]);
    } else {
      await me._router.navigate(["/sot/product"]);
    }
  }

  /** Opens the product screen and pre-stamps coupon_id/coupon_code/redemption_id so the basket item is never merged with a regular line of the same article. */
  public async OpenProductAsCouponLine(product: ISOTProductViewModel, couponId: string, couponCode: string, redemptionId: string, callback: () => void): Promise<void> {
    const me = this;
    me._product_component_buffer = {
      product: product,
      itemGroup: null,
      recipeLink: null,
      callback: callback,
      couponId: couponId,
      couponCode: couponCode,
      redemptionId: redemptionId,
    };
    await me._router.navigate(["/sot/product"]);
  }

  /** bestehenden Warenkorb Artikel zum Bearbeiten öffnen */
  public async OpenEditBasketProduct(product: Models.SOTBasketProductEditViewModel, callback: () => void): Promise<void> {
    const me = this;
    me._product_component_buffer = {
      product: product,
      itemGroup: null,
      recipeLink: null,
      callback: callback,
    }
    await me._router.navigate(["/sot/product"]);
  }

  /** Öffnet Manuelle Eingabe des Pagers */
  public async OpenManualPagerInput(callback: (success: boolean) => void): Promise<void> {
    const me = this;
    if (me._openDialogRef !== null) {
      return;
    }
    const config: NumberPadDialogComponentConfig = {
      accessabilty: me.Accessabilty,
      numberlength: me._pager_length
    };
    me._openDialogRef = me._sotDialogService.openNumberPadDialog(config);
    me._openDialogRef.afterClosed().subscribe((result) => {
      me._openDialogRef = null; // free buffer

      const success = result && result.length > 0;
      if (success) {
        me._setPager(result);
      }
      if (callback) {
        callback(success);
      }
    });
  }

  /** Opens the coupon manual-entry numberpad dialog; returns the entered code string or undefined on cancel. */
  public OpenManualCouponInput(): Promise<string> {
    const me = this;
    if (me._openDialogRef === null) {
      // couponLength removed — dialog manages min/max length internally via the numberPad directive
      const config: CouponNumberPadDialogConfig = {
        sot_service: me,
      };
      return new Promise<string>((resolve) => {
        me._openDialogRef = me._sotDialogService.openCouponNumberPadDialog(config);
        me._openDialogRef.afterClosed().subscribe((result) => {
          me._openDialogRef = null; // free buffer
          resolve(result);
        });
      });
    } else {
      me._coreLogService.error("SOTService.OpenManualCouponInput", "dialog was opened twice and canceled");
      return Promise.resolve(undefined);
    }
  }

  /** Resolves a multi-use 3–8-digit coupon via the local free_item_nr flow (no server validation). */
  private async _resolveMultiUseCoupon(trimmedCode: string): Promise<CouponRedeemResult> {
    const me = this;
    const code = +(trimmedCode);
      if (!(code > 0) || me._sotSettings.coupons_length !== code.toString().length) {
      me._coreLogService.error("SOTService._resolveMultiUseCoupon", "couponCode wrong length", trimmedCode.length);
      return CouponRedeemResult.CodeNotValid;
      }

    const products = me._getCouponSortimentProducts();
    if (!products) {
      return CouponRedeemResult.Error;
    }

    const couponProduct = products.find(product => product.free_item_nr == code);
    const redeemableState = me._checkCouponProductRedeemable(couponProduct);
    if (redeemableState) {
      return redeemableState;
    }

    return new Promise<CouponRedeemResult>((resolve) => {
      try {
        me.OpenProduct(couponProduct, null, null, () => {
          resolve(CouponRedeemResult.Done);
        });
      } catch (error) {
        const errMsg = (error && (error.codeText || error.code || error.message)) || "unknown";
        me._coreLogService.error("SOTService._resolveMultiUseCoupon", "OpenProduct.catch", errMsg);
        resolve(CouponRedeemResult.Error);
      }
    });
  }

  /** Products of the current assortment (order type); null for an unknown order type. */
  private _getCouponSortimentProducts(): Models.SOTProductViewModel[] | null {
    const me = this;
      if (me._orderType === orderTypes.inHouse) {
      return me._sotInHouseProducts;
      } else if (me._orderType === orderTypes.pickup) {
      return me._sotPickupProducts;
      }
    me._coreLogService.error("SOTService._getCouponSortimentProducts", "unknown ordertype", me._orderType);
    return null;
  }

  /** Returns the rejection reason for a coupon product, or null if it is redeemable. */
  private _checkCouponProductRedeemable(couponProduct: Models.SOTProductViewModel | undefined): CouponRedeemResult | null {
    const me = this;
      if (!couponProduct) {
      return CouponRedeemResult.CodeNotValid;
      }

      // ! Achtung _webshopSettingsDataModel muss vorher geladen werden
      const schedule_req = new ScheduledForRequest(me.GetOrderStart(), me.location, me._webshopSettingsDataModel, me._coreLanguageService, me._sotSettings.article_option);
      couponProduct.refreshSchedule(schedule_req);
      if (!couponProduct.schedule.active) {
      return CouponRedeemResult.CodeNotValid;
      }

      if (!couponProduct.is_available) {
      return CouponRedeemResult.ProductNotAvailable;
      }
    return null;
  }

  /** Single-use code: validation via the POS ($19/$20), then coupon line with redemption_id.
   *  Every POS rejection is reported generically as CodeNotValid (no oracle).
   *  showLoader is false for a scanned code: the guest stays on the scan page, like the other scans. */
  private async _resolveNineDigitCoupon(couponCode: string, showLoader: boolean): Promise<CouponRedeemResult> {
    const me = this;
        try {
      // Check before $19: the POS call consumes the code.
      const isAlreadyInCartByCode = me.GetBasket().products.some(
        (p) => p.coupon_code === couponCode
      );
      if (isAlreadyInCartByCode) {
        return CouponRedeemResult.AlreadyInCart;
      }

      const products = me._getCouponSortimentProducts();
      if (!products) {
        return CouponRedeemResult.Error;
      }

      if (showLoader) {
        me._sotDialogService.showLoaderDialog();
      }
      let validation: ISOTCouponValidation;
      try {
        validation = await me.SOTConnectionSendValidateCoupon(couponCode);
      } finally {
        if (showLoader) {
          me._sotDialogService.hideLoaderDialog();
        }
      }
      if (validation.error === "Interrupted") {
        return CouponRedeemResult.Interrupted;
      }
      if (!validation.success) {
        me._coreLogService.info("SOTService._resolveNineDigitCoupon", "rejected", validation.error);
        return CouponRedeemResult.CodeNotValid;
      }

      const articleNr = +validation.article_nr;
      const couponProduct = articleNr > 0 ? products.find(product => product.artikel_nr === articleNr) : undefined;
      const redeemableState = me._checkCouponProductRedeemable(couponProduct);
      if (redeemableState) {
        me._coreLogService.error("SOTService._resolveNineDigitCoupon", "release coupon: " + (couponProduct ? "product not redeemable (" + redeemableState + ")" : "no product with article_nr in assortment"),
          "article_nr: " + validation.article_nr + ", redemption_id: " + validation.redemption_id);
        me.SOTConnectionSendReleaseCoupon(validation.redemption_id);
        return redeemableState;
      }

      return await new Promise<CouponRedeemResult>((resolve) => {
        me.OpenProductAsCouponLine(couponProduct, validation.coupon_id, couponCode, validation.redemption_id, () => {
          const isInBasket = me.GetBasket().products.some((p) => p.redemption_id === validation.redemption_id);
          if (isInBasket) {
            resolve(CouponRedeemResult.Done);
          } else {
            me._coreLogService.info("SOTService._resolveNineDigitCoupon", "release coupon: coupon line not added to basket (guest cancelled)", "redemption_id: " + validation.redemption_id);
            me.SOTConnectionSendReleaseCoupon(validation.redemption_id);
            resolve(CouponRedeemResult.Cancelled);
          }
        }).catch((error) => {
          me._coreLogService.error("SOTService._resolveNineDigitCoupon", "OpenProductAsCouponLine.catch", (error && error.message) || "unknown");
          me.SOTConnectionSendReleaseCoupon(validation.redemption_id);
          resolve(CouponRedeemResult.Error);
          });
      });
        } catch (error) {
      const errMsg = (error && (error.codeText || error.code || error.message)) || "unknown";
      me._coreLogService.error("SOTService._resolveNineDigitCoupon", "catch", errMsg);
      return CouponRedeemResult.Error;
        }
  }

  /** Opens the "already in cart" dialog for a 9-digit coupon (coupon_code dedup). */
  public openCouponInCartDialog(callback: (goToBasket: boolean) => void): void {
    const me = this;
    if (me._openDialogRef === null) {
      me._openDialogRef = me._sotDialogService.openCouponInCartDialog(me);
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null;
        callback(result === "go_to_basket");
      });
    } else {
      callback(false);
    }
  }

  /** Dispatches a coupon code to POS validation (value 100.000.000–999.999.999) or the local free_item_nr flow (everything below).
   * @param showLoader false for a scanned code: no loader while the POS validates, like the other scans */
  public async openCouponProduct(couponCode: string, showLoader: boolean = true): Promise<CouponRedeemResult> {
    const me = this;
    try {
      const trimmedCode = (couponCode || "").trim();
      const codeLength = trimmedCode.length;

      if (codeLength < 3 || codeLength > 9 || !/^\d+$/.test(trimmedCode)) {
        me._coreLogService.error("SOTService.openCouponProduct", "couponCode wrong length or non-numeric", codeLength);
        return CouponRedeemResult.CodeNotValid;
      }

      // Detected by numeric value: "012345678" is 12345678 and stays on the local path.
      if (/^[1-9]\d{8}$/.test(trimmedCode)) {
        return await me._resolveNineDigitCoupon(trimmedCode, showLoader);
      }

      // Multi-use code: local free_item_nr resolution; coupons_length applies only here.
      return await me._resolveMultiUseCoupon(trimmedCode);
    } catch (error) {
      const errMsg = (error && (error.codeText || error.code || error.message)) || "unknown";
      me._coreLogService.error("SOTService.openCouponProduct", "catch", errMsg);
      return CouponRedeemResult.Error;
    }
  }

  //#region Voucher: pay & collect points
  private _voucherSelectedForPay: boolean = false;
  public set voucherSelectedForPay(selected: boolean) {
    this._voucherSelectedForPay = selected;
  }
  public get voucherSelectedForPay() {
    return this._voucherSelectedForPay;
  }

  public get remainingAmount(): number {
    const me = this;
    const totalSum = me.GetBasket().getTotalSum();
    const tipValue = totalSum * Utils.moneyRound((me._tip_percent / 100.0));
    let remainingAmount = totalSum + tipValue;
    if (me.voucher && me.voucherSelectedForPay) {
      remainingAmount -= (me.voucher.Amount + me.voucher.InviteAmount);
      if (remainingAmount < 0) {
        remainingAmount = 0; // bei überzahlung bleibt nichts offen
      }
    }
    return remainingAmount;
  }

  /** Dialog for scanning voucher
   *  @param action The voucher flow context. RedeemBenefit switches the dialog
   *  texts to the benefit-redemption langkeys. */
  private OpenScanVoucherDialog(action: VoucherActionType): Promise<"voucher" | "manual" | "cancel"> {
    const me = this;
    if (me._openDialogRef === null) {
      const config = {
        startScanVoucher: () => me.SOTConnectionSendScanVoucher(null),
        cancelScanVoucher: () => me.SOTConnectionSendCancelScanning(),
        scanVoucherSubscription: me.evScanVoucherDone,
        sot_service: me,
        isBenefitsRedemption: action === VoucherActionType.RedeemBenefit,
      }
      return new Promise<"voucher" | "manual" | "cancel">((resolve) => {
        me._openDialogRef = me._sotDialogService.openScanVoucherDialog(config);
        me._openDialogRef.afterClosed().subscribe((result) => {
          me._openDialogRef = null; // free buffer
          resolve(result);
        });
      });
    } else {
      me._coreLogService.error("SOTService.OpenScanVoucherDialog", "is try open twice");
      return Promise.resolve(undefined);
    }
  }

  /** Dialog for manual entry of the voucher code
   * @param action The voucher flow context. RedeemBenefit switches the dialog
   * itle to the benefit-redemption langkey.
   * @returns input value 
   */
  public OpenManualVoucherInput(action: VoucherActionType): Promise<string | "cancel"> {
    const me = this;
    if (me._openDialogRef === null) {
      const config = {
        sot_service: me,
        isBenefitsRedemption: action === VoucherActionType.RedeemBenefit,
      }
      return new Promise<string>((resolve) => {
        me._openDialogRef = me._sotDialogService.openVoucherNumberPadDialog(config);
        me._openDialogRef.afterClosed().subscribe((result) => {
          me._openDialogRef = null; // free buffer
          resolve(result);
        });
      });
    } else {
      me._coreLogService.error("SOTService.OpenManualVoucherInput", "is try open twice");
      return Promise.resolve(undefined);
    }
  }

  /** Dialog for manual entry of the voucher PIN
   * @returns input value 
   */
  //TODO on call add navigation (if result = cancel then navigate to checkout)
  public OpenManualVoucherPinInput(): Promise<string | "cancel"> {
    const me = this;
    return new Promise<string>((resolve) => {
      const config = {
        sot_service: me,
      }
      me._openDialogRef = me._sotDialogService.openVoucherPinNumberPadDialog(config);
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null; // free buffer
        resolve(result);
      });
    });
  }

  /** voucher pay dialog */
  public OpenVoucherPayDialog(): Promise<Models.PaymentType> {
    const me = this;
    if (me._openDialogRef === null) {
      return new Promise<Models.PaymentType>((resolve) => {
        me._openDialogRef = me._sotDialogService.openVoucherPayDialog({ sotService: me });
        me._openDialogRef.afterClosed().subscribe((result) => {
          me._openDialogRef = null; // free buffer
          resolve(result);
        });
      });
    } else {
      me._coreLogService.error("SOTService.OpenVoucherConfirmDialog", "canceled");
      return Promise.resolve(null);
    }
  }

  /**
   * Add voucher to order process, either for payment or redeeming benefits
   * @param action VoucherActionType: PayOrCollect or RedeemBenefit
   * @param navigateToCheckout if PayOrCollect canceled
   */
  public async AddVoucher(action: VoucherActionType, navigateToCheckout: boolean): Promise<void> {
    const me = this;
    try {
      // Check if voucher is already scanned
      if (me.voucher && me.voucher.Code) {
        await me._handleExistingVoucher(action, navigateToCheckout);
        return;
      }
      const result = await me.OpenScanVoucherDialog(action);
      if (!result) {
        // dialog konnte nicht doppelt geöffnet werden, ignorieren
        return;
      }
      if (result === "manual") {
        await me._handleManualVoucherScan(action, navigateToCheckout);
      } else if (result === "cancel") {
        me._voucherCanceled(navigateToCheckout);
      } else /* if (result === "voucher") */ {
        await me._handleExistingVoucher(action, navigateToCheckout);
      }
    } catch (error) {
      me._coreLogService.error("SOTService.AddVoucher", "error", error);
      me._voucherCanceled(navigateToCheckout);
    }
  }

  /** Handle manual voucher scan flow */
  private async _handleManualVoucherScan(action: VoucherActionType, navigateToCheckout: boolean): Promise<void> {
    const me = this;
    const voucherInputCode = await me.OpenManualVoucherInput(action);
    if (!voucherInputCode || voucherInputCode === "cancel") {
      me._voucherCanceled(navigateToCheckout);
      return;
    }
            const scanManualVoucherSubscription = me.evScanVoucherDone.subscribe(async (voucher) => {
              me._sotDialogService.hideLoaderDialog();
              scanManualVoucherSubscription.unsubscribe();
              if (voucher) {
        try {
          if (action === VoucherActionType.PayOrCollect) {
                const selectedPaymentType = await me.OpenVoucherPayDialog();
                me.setSelectedPaymentAndNavigate(selectedPaymentType);
          } else {// option = redeem benefit
            me._openBonusActionDialog();
          }

        } catch (error) {
          me._coreLogService.error("SOTService._handleManualVoucherScan", "OpenVoucherPayDialog error", error);
          scanManualVoucherSubscription.unsubscribe();
        }
              } else {
        me.OpenVoucherCouldNotBeReadDialog((retry) => {
          me._handleVoucherRetry(retry, action, navigateToCheckout);
                });
              }
            });
            me.SOTConnectionSendScanVoucher(voucherInputCode);
            if (me.hasComunicationStatus(SOTComunicationStatus.wait_scan_voucher)) {
              me._sotDialogService.showLoaderDialog();
            }
          }

  /** Handle existing voucher payment flow */
  private async _handleExistingVoucher(action: VoucherActionType, navigateToCheckout: boolean): Promise<void> {
    const me = this;
        if (me.voucher) {
      if (action === VoucherActionType.PayOrCollect) {
          const selectedPaymentType = await me.OpenVoucherPayDialog();
        me.setSelectedPaymentAndNavigate(selectedPaymentType);
        } else {
        // option = redeem benefit
        me._openBonusActionDialog();
      }

    } else {
          // retry
          me.OpenVoucherCouldNotBeReadDialog((retry) => {
        me._handleVoucherRetry(retry, action, navigateToCheckout);
          });
        }
      }

  /** voucher scan /entry was canceled 
   * reset voucher selected and navigate to checkout*/
  private _voucherCanceled(navigateToCheckout: boolean) {
    const me = this;
    me.voucherSelectedForPay = false;
    // ! voucher bleibt hinterlegt
    if (navigateToCheckout) {
    me.GoToCheckout();
    }// else do nothing
  }

  /** Handle voucher retry logic */
  private _handleVoucherRetry(retry: boolean, action: VoucherActionType, navigateToCheckout: boolean): void {
    const me = this;
    if (retry) {
      me.AddVoucher(action, navigateToCheckout);
    } else {
      me._voucherCanceled(navigateToCheckout);
    }
  }

  public GoToPagerAndAddVoucher() {
    const me = this;
    if (me.GetSettings().use_pager) {
      me._router.navigate(["/sot/pager"]);
    } else {
      me.AddVoucher(VoucherActionType.PayOrCollect, true);
    }
  }
  //#endregion

  //#region Benefit redeem

  public get canDisplayRedeemBenefitBtn(): boolean {
    const me = this;
    return me.GetSettings().allow_benefits_at_sot && me._sotBonusActions && me._sotBonusActions.length > 0;
  }

  /** 
   * Loads all bonus actions that are currently active, valid for the user's location,
   * and have at least one valid benefit.
   * Applies date checks, deletion checks, location whitelist/blacklist (isInLocationList) rules
   * and filters out actions without usable benefits.
   */
  private async getBonusActions(): Promise<Models.SOTBonusAction[]> {
    const me = this;
    try {
      const bonusActionObserver: IObjectObserver<BonusactionDataModel> = ObjectObserverFactory.GetObjectObserverByInjectionHelper<BonusactionDataModel>(ContentType.Bonusaction, me._circularInjectionHelper);
      const currentDate: moment.Moment = moment();
      const bonusActionDataModels = await bonusActionObserver.GetObjectsByFilter(bonusAction => {
        if (bonusAction.deleted || !bonusAction.locations) return false;
        if (currentDate.valueOf() < bonusAction.start_at.valueOf()) return false;
        if (bonusAction.has_total_end_at && currentDate.valueOf() > bonusAction.total_end_at.valueOf()) return false;
        const isInLocationList = bonusAction.locations.includes(me.location);
        if (bonusAction.locations_participation) {
          return isInLocationList;
        } else {
          return !isInLocationList;
        }
      });

      const bonusActions = await Promise.all(
        bonusActionDataModels.map(async (model) => {
          try {
            const benefits = await me.getBenefitsForBonusAction(model.id);
            return new Models.SOTBonusAction(model, benefits);
          } catch (error) {
            me._coreLogService.error("SOTService.getBonusActions", `Failed to load benefits for action ${model.id}`, error);
            return null;
          }
        })
      );
      const validBonusActions: Models.SOTBonusAction[] = [];
      bonusActions.forEach(action => {
        if (action && action.benefits && action.benefits.length > 0) {
          validBonusActions.push(action);
        }
      });
      return validBonusActions;
    } catch (error) {
      me._coreLogService.error("SOTService.getBonusActions", "Failed to get bonus actions", error);
      throw error;
    }
  }

  /**
   * Get benefits for a specific bonus action
   * @param bonusActionId The ID of the bonus action
   */
  private async getBenefitsForBonusAction(bonusActionId: number): Promise<Models.SOTBonusActionBenefit[]> {
    const me = this;
    try {
      const bonusBenefitObserver: IObjectObserver<BonusactionBenefitDataModel> = ObjectObserverFactory.GetObjectObserverByInjectionHelper<BonusactionBenefitDataModel>(ContentType.BonusactionBenefit, me._circularInjectionHelper);
      const benefitDataModels = await bonusBenefitObserver.GetObjectsByFilter(b => b.bonusaction_id === bonusActionId && !b.deleted);
      const benefits = benefitDataModels.map(model => new Models.SOTBonusActionBenefit(model));
      const valid: Models.SOTBonusActionBenefit[] = [];
      const dropped: Models.SOTBonusActionBenefit[] = [];
      benefits.forEach(benefit => {
        if (me._hasBenefitProduct(benefit.productID)) {
          valid.push(benefit);
        } else {
          dropped.push(benefit);
        }
      });
      if (dropped.length > 0) {
        me._coreLogService.error("SOTService.getBenefitsForBonusAction", `Action ${bonusActionId}: ${dropped.length} Benefit(s) without resolvable product hidden`, dropped.map(b => b.productID));
      }
      return valid;
    } catch (error) {
      me._coreLogService.error("SOTService.getBenefitsForBonusAction", `Failed to get benefits for bonus action ${bonusActionId}`, error);
      throw error;
    }
  }

  /**
   * Assigns user bonus points from the scanned voucher to each local bonus action.
   * - Finds matching voucher action by ID for each local action
   * - Sets user points using minimal ISOTBonuspointsView
   * - Enables correct display and logic for user-specific bonus points
   */
  private _setUserBonusActionPoints(): boolean {
    const me = this;
    if (!me.voucher) {
      me._coreLogService.error("SOTService._setUserBonusActionPoints", "No voucher available");
      return false;
    }
    if (!me._sotBonusActions || me._sotBonusActions.length === 0) {
      me._coreLogService.error("SOTService._setUserBonusActionPoints", "No local bonus actions loaded");
      return false;
    }
    if (!me.voucher.BonusActions) {
      me._coreLogService.error("SOTService._setUserBonusActionPoints", "No bonus actions found in the voucher");
      return false;
    }
    const userBonusPointsView = {
      hasData: true,
      actions: me.voucher.BonusActions.map(bonusAction => ({ id: bonusAction.ID, points: bonusAction.Points }))
    };
    me._sotBonusActions.forEach(localAction => {
      localAction.loadUserBonusActionPoints(userBonusPointsView);
    });
    return true;
  }

  //Get benefit product by benefit product ID
  public getBenefitProduct(benefitProductId: number): Models.SOTProductViewModel | null {
    const me = this;
    const products = me._getBenefitProductPool();
    if (!products) { return null; }
    const benefitProduct = products.find(product => product.product_id === benefitProductId);
    if (!benefitProduct) {
      me._coreLogService.error("SOTService.getBenefitProduct", `Benefit product with ID ${benefitProductId} not found for order type ${me._orderType}`);
      return null;
    }
    return benefitProduct;
  }

  /** Checks whether the given benefit product ID exists in the current product pool.*/
  private _hasBenefitProduct(benefitProductId: number): boolean {
    const me = this;
    const products = me._getBenefitProductPool();
    if (!products) { return false; }
    return products.some(product => product.product_id === benefitProductId);
  }

  /** Get benefit product pool based on the current order type */
  private _getBenefitProductPool(): Models.SOTProductViewModel[] | null {
    const me = this;
    if (me._orderType === orderTypes.inHouse) { return me._sotInHouseProducts; }
    if (me._orderType === orderTypes.pickup) { return me._sotPickupProducts; }
    me._coreLogService.error("SOTService._getBenefitProductPool", "unknown ordertype", me._orderType);
    return null;
  }

  /** Check whether the benefit items in the shopping
   * cart are still covered by the user’s points.*/
  public hasUncoveredBenefits(): boolean {
    const me = this;
    const basketProducts = me._sotBasket.products;
    return me._sotBonusActions.some(action => action.isUncovered(basketProducts));
  }

  //TODO POS state
  private _redeemBenefitStateFailed: boolean = false;
  /** Beim einlösen der Benefits am POS kamm es zu einem Problem */
  public get redeemBenefitStateFailed(): boolean {
    return this._redeemBenefitStateFailed;
  }

  /** Opens the bonus action dialog to edit a benefit 
   *  @see _openBonusActionDialog
  */
  public openBonusActionDialogToEditBenefit(isBenefitItem: boolean) {
    const me = this;
    if (!isBenefitItem) { return }
    me._openBonusActionDialog();
  }

  /** Opens the combined choice dialog for Redeem Benefit or Coupon */
  public openCombinedChoiceDialog(): Promise<SOTDialogResult> {
    const me = this;
    if (me._openDialogRef !== null) {
      me._coreLogService.error("SOTService.openCombinedChoiceDialog", "is try open twice");
      return Promise.resolve(undefined);
    }
    return new Promise<SOTDialogResult>((resolve) => {
      me._openDialogRef = me._sotDialogService.openCouponOrBenefitDialog(me);
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null;
        resolve(result);
      });
    });
  }

  /** Open bonus action dialog */
  private _openBonusActionDialog(): void {
    const me = this;
    if (me._openDialogRef !== null) { return; }
    if (!me._setUserBonusActionPoints()) { return; }
    const config = {
      bonusActions: me._sotBonusActions,
      sot_service: me
    }
    me._openDialogRef = me._sotDialogService.openBonusActionDialog(config);
    me._openDialogRef.afterClosed().subscribe(() => {
      me._openDialogRef = null;
    });
  }

  /** open points coverage warning dialog */
  public openPointsCoverageWarningDialog() {
    const me = this;
    if (me._openDialogRef !== null) { return }
    me._openDialogRef = me._sotDialogService.openPointsCoverageWarningDialog(me);
    me._openDialogRef.afterClosed().subscribe(() => {
      me._openDialogRef = null; // free buffer
    });
  }

  //#endregion Benefit redeem

  public setSelectedPaymentAndNavigate(selectedPayment: Models.PaymentType) {
    const me = this;
    if (selectedPayment) {
      me.SetPayType(selectedPayment);
      me.StartPayment();
    } else {
      me.GoToCheckout();
    }
  }

  /** open voucher warning dialog*/
  public async OpenVoucherCouldNotBeReadDialog(callback: (rescan: boolean) => void) {
    const me = this;
    if (me._openDialogRef === null) {
      me._openDialogRef = me._sotDialogService.OpenVoucherCouldNotBeReadDialog(me);
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null; // free buffer
        if (result === "rescan") {
          callback(true);
        } else if (result === "continue") {
          callback(false);
        }
      });
      return true;
    } else {
      return false;
    }
  }
  //# end Voucher

  public OpenProductDialog(product: Models.SOTProductViewModel, as_info: boolean, callback: (status: any) => void): boolean {
    const me = this;
    if (me._openDialogRef === null) {
      const config = {
        product: product,
        is_info_type: as_info,
        sot_service: me,
      };
      me._openDialogRef = me._sotDialogService.openSingleProductDialog(config);
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null; // free buffer
        if (callback) {
          callback(result);
        }
      });
      return true;
    } else {
      return false;
    }
  }

  public OpenExtraProductInfoDialog(product: Models.SOTProductViewModel, is_extra_product: boolean, callback: (status: any) => void, extra_product_label?: string): boolean {
    const me = this;
    if (me._openDialogRef === null) {
      const config = {
        product: product,
        is_info_type: true,
        is_extra_product: is_extra_product,
        extra_product_label: extra_product_label,
        sot_service: me,
        useModeOne: me.useModeOne(),
      };
      me._openDialogRef = me._sotDialogService.openSingleProductDialog(config);
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null; // free buffer
        if (callback) {
          callback(result);
        }
      });
      return true;
    } else {
      return false;
    }
  }

  /**
   * Display the sub-extra items and additional info (optional)
   * @param tempProduct 
   * @param extra 
   * @param extragroup 
   * @param callback 
   * @returns whether a dialog is currently open
   */
  public OpenExtraProductSubDialog(tempProduct: Models.SOTBasketProductViewModel, product: Models.SOTProductViewModel, extra: Models.SOTProductExtraItemViewModel, extragroup: Models.SOTProductExtraGroupViewModel, callback: (status: any) => void): boolean {
    const me = this;
    if (me._openDialogRef === null) {
      const config: ProductSubDialogConfig = {
        tempProduct: tempProduct,
        product: product,
        extra: extra,
        extragroup: extragroup,
        sot_service: me,
      };
      const dialogRef = me._sotDialogService.openProductSubDialog(config);
      me._openDialogRef = dialogRef;
      dialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null; // free buffer
        if (result && result.status == "open_info_dialog") {
          me.OpenExtraProductInfoDialog(result.data.product, true, () => {
            me.OpenExtraProductSubDialog(tempProduct, product, extra, extragroup, callback);
          }, result.data.sub_product_label);
        } else if (callback) {
          callback(result && result.status == "done");
        }
      });
      return true;
    } else {
      return false;
    }
  }

  public OpenProductRecommendationDialog(itemGroup: Models.SOTItemGroupViewModel, isItemTagEnabled: boolean, callback: (status: any) => void): boolean {
    const me = this;
    if (me._openDialogRef === null) {
      const config = {
        recommendations: itemGroup.recommendations,
        isItemTagEnabled: isItemTagEnabled,
        hasTagSelected: me.GetProductTagFilter().length > 0,
        sot_service: me,
      };
      me._openDialogRef = me._sotDialogService.openProductRecommendationDialog(config);
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null; // free buffer
        if (callback) {
          callback(result);
        }
      });
      return true;
    } else {
      return false;
    }
  }

  /** Öffnet den Dialog "Zahlung fehlgeschlagen" und startet den timer für die Anzeige des inactive Dialogs*/
  public OpenPayFailDialog(pay_at_pos: boolean, callback: (status: any) => void): boolean {
    const me = this;
    if (me._openDialogRef === null) {
      me._openDialogRef = me._sotDialogService.openPayFailDialog(pay_at_pos, me);
      me.inactive_dlg_start();
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null; // free buffer
        me.inactive_dlg_stop();
        if (callback) {
          callback(result);
        }
      });
      return true;
    } else {
      return false;
    }
  }

  /**open product tag filter dialog*/
  public openProductTagFilterDialog(): Promise<number[]> {
    const me = this;

    if (me._openDialogRef === null) {
      return new Promise<number[]>((resolve) => {
        const config = {
          productTags: me.GetProductTags(),
          selectedProductTags: me.GetProductTagFilter(),
          sot_service: me,
        };
        me._openDialogRef = me._sotDialogService.openArticleTagFilterDialog(config);
        me._openDialogRef.afterClosed().subscribe((result) => {
          me._openDialogRef = null; // free buffer
          me.setForceTagFilterClosed(me._openDialogRef === null);
          resolve(result);
        });
      });
    } else {
      me._coreLogService.error("SOTService.openArticleTagFilterDialog", "dialog canceled");
      return Promise.resolve(null);
    }
  }

  /** user can confirm to reset tag filter for single extra*/
  public openResetExtraTagFilter(): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve) => {
      me._openDialogRef = me._sotDialogService.openResetExtraTagFilter(me);
      me._openDialogRef.afterClosed().subscribe((result) => {
        me._openDialogRef = null; // free buffer
        if (result === "yes") {
          resolve(true);
        } // else result == "no" > ignore 
        me._openDialogRef = null;
      });
    });
  }

  public async openAccessabiltyDialog(isModeOne: boolean, isModeTwo: boolean): Promise<{ useModeOne: boolean, useModeTwo: boolean } | null> {
    // Verhindert Mehrfachaufrufe, wenn der Dialog bereits geöffnet ist
    if (this._openDialogRef) {
      return null;
    }
    const config = {
      sot_service: this,
      isModeOne,
      isModeTwo,
    };
    this._openDialogRef = this._sotDialogService.openAccessabiltyDialog(config);
    try {
      const result = await this._openDialogRef.afterClosed().toPromise();
      return result || null;
    } catch (error) {
      console.error('Error in openAccessabiltyDialog:', error);
      return null;
    } finally {
      this._openDialogRef = null;
    }
  }

  public get hasTagSelected() {
    const me = this;
    return me.GetProductTagFilter() ? me.GetProductTagFilter().length > 0 : false;
  }

  private _resetTagFilterExtraIds: number[] = [];
  /** set list of extra IDs for resetting the tag filter */
  public setResetTagFilterExtraIds(extraId: number): number[] {
    const me = this;
    if (me._resetTagFilterExtraIds.indexOf(extraId) === -1) {
      me._resetTagFilterExtraIds.push(extraId);
    }
    return me._resetTagFilterExtraIds;
  }

  /**check whether the user reseted tag filter for single extra */
  public isResetTagFilterActive(extraId: number): boolean {
    const me = this;
    let resetedByUser: boolean = false;
    if (me._resetTagFilterExtraIds.length > 0) {
      if (me._resetTagFilterExtraIds.indexOf(extraId) !== -1) {
        resetedByUser = true;
      }
    }
    return resetedByUser;
  }

  /** empty resetTagFilter list on navigation */
  public setDefaultResetTagFilterEmpty() {
    const me = this;
    if (me._resetTagFilterExtraIds.length > 0) {
      return me._resetTagFilterExtraIds = [];
    }
  }

  private _forceTagFilterClosed: boolean = false;
  /**forced filterTag: dialog loaded and closed */
  public getForceTagFilterClosed() {
    return this._forceTagFilterClosed;
  }

  /**forced tag fiter dialog closed
   * avoid opening the filter tag dialog every time on navigate back
   * @param forceLoaded dialog was loaded and closed
   */
  public setForceTagFilterClosed(forceLoaded: boolean) {
    const me = this;
    return me._forceTagFilterClosed = forceLoaded;
  }

  /** "setForceTagFilterLoaded" default value is false*/
  public setDefaultForcedTagFilterClosed() {
    const me = this;
    me.setForceTagFilterClosed(false);
  }

  /** Startet wenn die Zahlung erfolgreich war und Countdown-Timer gestoppt wurde
   * wird in paymentConfirmation component aufgerufen
   */
  public StartInactiveDialogTimer() {
    this.inactive_dlg_start();
  }
  /** Nach Ablauf beendet inactiver Dialog und Countdown-Timer
   * wird in paymentConfirmation component aufgerufen
   */
  public StopInactiveDialogTimer() {
    this.inactive_dlg_stop();
  }

  /** buffert die Daten für die Menu Componente (ab Angular 7.2.0 kann es auch mit der naviagation als state übergeben werden) */
  private _menu_component_buffer: { scroll_position: number } = null;
  /**
   * stellt die Daten für die Menu Componente (ab Angular 7.2.0 kann es auch mit der naviagation als state übergeben werden)
   * ! darf nur von der menu componente aufgerufen werden
   */
  public GetAndClearMenuComponentBuffer(): { scroll_position: number } {
    const buffer = this._menu_component_buffer;
    this._menu_component_buffer = null;
    return buffer;
  }

  /** naviegiert zum menu/homescreen und resettet sich optional für den nächsten einkauf, wenn orderType gesetzt wird
   * @param order_type wenn null, dann bleibt es wie es ist, mit einem richtigen order type sollte durch ordertype komponente aufgerufen werden
   * @param scroll_position (optional) scrollt beim öffnen an die position (gedacht für die rück navigation)
   */
  public async GoToMenu(order_type: null | orderTypes.inHouse | orderTypes.pickup, scroll_position?: number): Promise<void> {
    const me = this;
    // Reset wenn ordertype gesetzt wird.
    if (order_type != null) {
      me.CleanUp();
      me.SetOrderType(order_type);
      //TODO auslagern von filterActiveEventForMoment
      me.UseNewAvailability();
      me.inactive_dlg_start();
    } else if (scroll_position != null) {
      me._menu_component_buffer = {
        scroll_position: scroll_position,
      }
    }
    // Wenn PayAtCard, dann prüfen ob bereits eine Karte gescannt wurde
    if (me.payAtCard && !me._card_no) {
      await me._router.navigate(["/sot/cardscan"]);
    } else {
      // umleitung auf den menu/homescreen
      await me._router.navigate(["/sot/menu"]);
    }
  }

  /** buffert die Daten für die Checkout Componente (ab Angular 7.2.0 kann es auch mit der naviagation als state übergeben werden) */
  private _checkout_component_buffer: { scroll_position: number } = null;
  /**
   * stellt die Daten für die Checkout Componente (ab Angular 7.2.0 kann es auch mit der naviagation als state übergeben werden)
   * ! darf nur von der checkout componente aufgerufen werden
   */
  public GetAndClearCheckoutComponentBuffer(): { scroll_position: number } {
    const buffer = this._checkout_component_buffer;
    this._checkout_component_buffer = null;
    return buffer;
  }

  /** naviegiert zum chackout/bestellübersicht 
   * @param scroll_position (optional) scrollt beim öffnen an die position (gedacht für die rück navigation)
  */
  public async GoToCheckout(scroll_position?: number): Promise<void> {
    const me = this;
    if (scroll_position != null) {
      me._checkout_component_buffer = {
        scroll_position: scroll_position,
      }
    }
    // umleitung auf den checkout/bestellübersicht
    await me._router.navigate(["/sot/checkout"]);
  }

  /** Leitet -wenn eingestellt- zum Pager entnehmen, ansonsten direkt zum Cardscan */
  public async GoToPager(): Promise<void> {
    const me = this;
    if (me.GetSettings().use_pager) {
      me._router.navigate(["/sot/pager"]);
    } else {
      if (me.GetPayType() === Models.PaymentType.atCard) {
        me.GoToOrderCardScan();
      } else {
        me.StartPayment();
      }
    }
  }

  /** Leitet zum Bezahl Cardscan weiter für Vorgangskartenzahlung*/
  public async GoToOrderCardScan(): Promise<void> {
    const me = this;
    if (me.GetPayType() === Models.PaymentType.atCard) {
      me._router.navigate(["/sot/ordercardscan"]);
    } else {
      me._coreLogService.error("SOTService.GotoOrderCardScan", "Tried to go to processcard scanning with incorrect Paymentype", me.GetPayType());
    }
  }

  /** gibt den Basket an das SOT/POS ab zum bezahlen und leitet weiter zum payment page */
  public async TryCardPayment(): Promise<void> {
    const me = this;
    if (me.isCardUnderLimit) {
      me.SOTConnectionSendBasket();
    } else {
      me.OpenCardlimitDialog(() => {
        me.GoToCheckout();
      });
    }
  }

  /** gibt den Basket an das SOT/POS ab zum bezahlen und leitet weiter zum payment page */
  public async StartPayment(): Promise<void> {
    const me = this;
    me.inactive_dlg_stop();
    await me._router.navigate(["/sot/payment"]);
    me.SOTConnectionSendBasket();
  }

  /** leitet auf die zahlungsbestätigungsseite, wo der hinweis zumabholen steht */
  private async GoToPaymentConfimation(): Promise<void> {
    await this._router.navigate(["/sot/paymentconfirmation"]);
  }

  public async GoToCouponScan(): Promise<void> {
    await this._router.navigate(["/sot/coupon"]);
  }

  /** bereinigt die Anwender eingaben */
  private CleanUp(): void {
    const me = this;
    if (me._openDialogRef) {
      me._openDialogRef.close("cancel");
    }
    me._productTagFilter = [];
    me._order_no = "";
    me._order_open_amount = 0;
    me._redeemBenefitStateFailed = false;
    me._order_bonus_points = [];
    me._tip_percent = 0;
    me._pager_no = 0;
    me._card_no = "";
    me._card_amount = 0;
    me._card_limit = 0;
    me._voucher = null;
    me._voucherSelectedForPay = false;
    me.ResetPaytype();
    // Bestellungs zeitpunkt wird bestimmt, wenn es zum ersten mal die Artikel ermittelt
    me._sotOrderStart = null;
    // Nothing is reserved after $03; on abort/timeout reset the redemptions are released.
    me._releaseAllReservedCoupons();
    me._sotBasket.clear();
    if (me._sotBonusActions) {
      me._sotBonusActions.forEach(action => action.resetUserState());
    }
    me.setDefaultForcedTagFilterClosed();
    me.setDefaultResetTagFilterEmpty();
    me._confirmedAge = -1;
    me._in_house_pe_event = -1;
    me._pickup_pe_event = -1;
    // todo later reset user
  }

  private _lastSortLanguageKey: string;
  /** Resortiert die Artikel wenn es alphabetisch sortiert werden muss
   * notwendig, wenn die Sprache geändert wird
   * @returns true, wenn eine sortierung notwendig war und erfollt ist, sonst false
   */
  public ResortProducts(): boolean {
    const me = this;
    let resorted = false;
    if (me._sotSettings.model.sort_type == MenuSettings_SortType.Alphabetic && this._lastSortLanguageKey != this.GetCurrentLanguage()) {
      if (me._sotInHouseItemGroups) {
        me._sotInHouseItemGroups.forEach(item_group => {
          item_group.products.sort(me.product_sort);
        });
      }
      if (me._sotPickupItemGroups) {
        me._sotPickupItemGroups.forEach(item_group => {
          item_group.products.sort(me.product_sort);
        });
      }
      this._lastSortLanguageKey = this.GetCurrentLanguage()
      resorted = true;
    } // else keine sortierung notwendig, sie wurden in LoadServerData sortiert
    return resorted;
  }

  /** Einstellungen für den SOT */
  public GetSettings(): Models.SOTSettingsViewModel {
    return this._sotSettings;
  }

  /** momentan angewandte Preisebene */
  public GetServicePE(): number {
    const me = this;
    let result: number = 0;
    // for service check
    if (me._orderType == orderTypes.inHouse) {
      let currentevent: IEvent = null;
      if (me.inHouse_events.length > 0) {
        currentevent = me._eventManagerService.filterActiveEventNow(me.inHouse_events, me._webshopSettingsDataModel.holidays);
      }//else keine events vorhanden
      if (!Utils.IsNull(currentevent)) {
        result = currentevent.pricelevel;
      } else {
        result = me.in_house_pe;
      }
    } else if (me._orderType == orderTypes.pickup) {
      let currentevent: IEvent = null;
      if (me.pickup_events.length > 0) {
        currentevent = me._eventManagerService.filterActiveEventNow(me.pickup_events, me._webshopSettingsDataModel.holidays);
      }//else keine events vorhanden
      if (!Utils.IsNull(currentevent)) {
        result = currentevent.pricelevel;
      } else {
        result = me.pickup_pe;
      }
    } else {
      //result bleibt 0, Standardpreisebene wird verwendet
      me._coreLogService.error("SOTService.GetServicePE", "unknown ordertype", me._orderType);
    }
    return result;
  }

  private _SOTAccessabilty: SOTAccessabilty = new SOTAccessabilty();

  /** todo */
  public get Accessabilty(): SOTAccessabilty {
    return this._SOTAccessabilty;
  }

  /** momentan angewandte Benutzer Preisebene */
  public GetUserPE(): number {
    return -1; // -1 > use servicePE // todo implement with user
  }

  /** momentan besten Discount in vollen Prozent */
  public GetBestDiscountPercent(): number {
    return Math.max(this.userDiscountPercent, this.webvoucherDiscountPercent);
  }

  /** rabatt, den der kunde hat in prozent */
  private get userDiscountPercent(): number {
    return 0; // todo wenn kunden sich amleden können
  }

  /** rabatt, des webgutscheins in prozent */
  private get webvoucherDiscountPercent(): number {
    const me = this;
    if (me.voucher && me.voucher.Discount) {
      return me.voucher.Discount
    } else {
      return 0;
    }
  }

  /** Startseiten für den SOT */
  public GetStartpages(): Models.SOTStartpageViewModel[] {
    return this._sotStartpages;
  }

  /** wird initial gesetzt, damit zur weiteren laufzeit es nicht immer übergeben werden muss
   * Achtung es sollte sofort nach dem Startscreen gesetz werden und nicht mehr während der bestellung geändert werden
   * @param orderType der order type sollte unterstützt werden (sonst error log)
   */
  public SetOrderType(orderType: orderTypes.inHouse | orderTypes.pickup) {
    const me = this;
    if (orderType != orderTypes.inHouse && orderType != orderTypes.pickup) {
      me._coreLogService.error("SOTService.SetCurrentOrderType", "not supported ordertype", orderType);
    } else if ((orderType == orderTypes.inHouse && !me.hasInHouseOption) || (orderType == orderTypes.pickup && !me.hasPickupOption)) {
      me._coreLogService.error("SOTService.SetCurrentOrderType", "ordertype forbidden by customer", orderType);
    }
    me._orderType = orderType;
  }

  /** Der momentane Bestelltyp */
  public GetOrderType(): orderTypes {
    return this._orderType;
  }

  /** kann bestimmen, wie der anwender zahlen möchte
   * Achtung wird nicht geändert, wenn das paymenttype nicht erlaubt ist
   * @param payType neuer paytype
   * @returns {boolean} true, wen geändert wurde, sonst false
   */
  public SetPayType(payType: Models.PaymentType): boolean {
    const me = this;
    if (payType !== me._payType) {
      if ((payType === Models.PaymentType.ecTerminal && me._payAtTerminal)
        || (payType === Models.PaymentType.atPOS && me._payAtPOS)
        || (payType === Models.PaymentType.atCard && me._payAtCard)
        || (payType === Models.PaymentType.voucherPay && me._allowVoucherPay)) {
        me._payType = payType;
        return true; // break here
      } else {
        me._coreLogService.error("SOTService.SetPayType", "payType not allowed", payType, me._payAtTerminal, me._payAtPOS);
      }
    }

    return false; // no changes
  }

  /** Der momentan gewählte zahlungsart */
  public GetPayType(): Models.PaymentType {
    return this._payType;
  }

  /** Setzt die gewählte zahlungsart zurück */
  public ResetPaytype(): void {
    const me = this;
    if (me._payAtTerminal) {
      me._payType = Models.PaymentType.ecTerminal;
    } else /* if (me._payAtPos) */ {
      me._payType = Models.PaymentType.atPOS;
    }
  }

  /** gets current active advertisment */
  public GetAdvertisments(): Models.SOTAdvertisementViewModel[] {
    const me = this;
    // for service check
    const inHouse = me._orderType == orderTypes.inHouse;
    const pickup = me._orderType == orderTypes.pickup;
    // for language check
    const language = me.GetCurrentLanguage();
    // for limitation check
    const now = moment();
    let now_day = now.day();
    if (now_day == 0) now_day = 7; // so ist montag=1 bis sonntag=7
    now_day = now_day - 1; // so ist montag=0 bis sonntag=6
    now_day = Math.pow(2, now_day); // so ist montag=1 bis sonntag=64
    const now_hms = Utils.GetHMSFromMoment(now);
    // führe den filter durch
    return me._sotAdvertisements.filter((adv) => {
      const adv_model = adv.model;
      return ((inHouse && adv_model.use_in_house_service)
        || (pickup && adv_model.use_pickup_service))
        && (adv_model.languages.indexOf(language) > -1)
        && ((!adv_model.use_limitation)
          || (adv_model.use_limitation && ((adv_model.limitation_days & now_day) == now_day)
            && adv_model.limitation_start_hms <= now_hms
            && adv_model.limitation_end_hms >= now_hms))
    });
  }

  /** zum bestelltyp passende empfehlungen  */
  private GetRecommendationRules(): Models.SOTRecommendationRulesDataModel[] {
    const me = this;
    let result = new Array<Models.SOTRecommendationRulesDataModel>();
    // for service check
    if (me._orderType == orderTypes.inHouse) {
      result = me._sotInHouseRecommendationRules;
    } else if (me._orderType == orderTypes.pickup) {
      result = me._sotPickupRecommendationRules;
    } else {
      me._coreLogService.error("SOTService.GetItemGroups", "unknown ordertype", me._orderType);
    }
    return result;
  }

  /** webshopSettings werden auch vom SOT für die Zeitsteuerung verwendet */
  private _webshopSettingsDataModel: WebshopSettingsDataModel2;
  /** webshopSettings werden auch vom SOT für die Zeitsteuerung verwendet */
  private _webshopSettingsObsServ: IObjectObserver<WebshopSettingsDataModel2>;
  /** webshopSettings werden auch vom SOT für die Zeitsteuerung verwendet */
  private InitWebshopSettingsObsServ(): void {
    this._webshopSettingsObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<WebshopSettingsDataModel2>(ContentType.WebshopSettings, this._circularInjectionHelper);
  }
  /** webshopSettings werden auch vom SOT für die Zeitsteuerung verwendet */
  public async getWebshopSettingsDataModel(): Promise<WebshopSettingsDataModel2> {
    if (!this._webshopSettingsDataModel) {
      this._webshopSettingsDataModel = await this._webshopSettingsObsServ.GetObject(WebshopSettingsDMKey);
    }
    return this._webshopSettingsDataModel;
  }

  /** gibt die empfehlungen heraus, nur verfügbare und gemischt */
  public GetRecommendations(): Models.SOTProductViewModel[] {
    const me = this;
    let recommendationIds = new Array<number>();
    const recommendationRules = me.GetRecommendationRules();
    if (recommendationRules.length > 0) {
      const basket = me.GetBasket();
      const products = basket.products;
      // ermitteln der artikel die empfehlen werden sollen
      recommendationRules.forEach(rule => {
        // Sind ALLE erforderlichen Warengruppen für die Regel vorhanden?
        const ruleNeedMatchs = rule.needed_article_group_ids.every(needed_article_group_id => {
          return products.some(product => product.model.model.inGroup([needed_article_group_id]));
        });
        // Sind KEINE verbotenen Warengruppen für die Regel vorhanden?
        const ruleMissMatchs = !products.some(product => {
          return product.model.model.inGroup(rule.missing_article_group_ids);
        });

        if (ruleNeedMatchs && ruleMissMatchs) {
          recommendationIds = recommendationIds.concat(rule.product_ids);
        }
      });
      // entfernen der artikel des warenkorbs aus den empfehlungen
      const basket_product_ids = products.map((product) => product.model.product_id);
      recommendationIds = recommendationIds.filter((product_id) => !basket_product_ids.includes(product_id) && me.AvailabilityIsProductAvailable(product_id));
    }

    let products = me._sotInHouseProductsMap;
    if (me._orderType == orderTypes.pickup) {
      products = me._sotPickupProductsMap;
    }
    // Filter Scheduled
    // ! Achtung _webshopSettingsDataModel muss vorher geladen werden
    const schedule_req = new ScheduledForRequest(me.GetOrderStart(), me.location, me._webshopSettingsDataModel, me._coreLanguageService, me._sotSettings.article_option);

    let recommendations = recommendationIds.map((product_id) => {
      const product = products[product_id];
      if (product) {
        product.refreshSchedule(schedule_req);
      }
      return product;
    });
    // Produkte die nicht vorhanden sind, ignorieren und scheduled beachten.
    recommendations = recommendations.filter((product) => product !== undefined && product.schedule.active); // empfehlungen nur aktive
    // product tag filter
    if (me._productTagFilter && me._productTagFilter.length > 0) {
      const hasSomeProductTags = (product_tag_ids: number[]): boolean => {
        return me._productTagFilter.every((id => product_tag_ids.indexOf(id) >= 0))
      }
      recommendations = recommendations.filter((product) => hasSomeProductTags(product.product_tag_ids))
    }
    // mischen damit sie nicht sortiert ausgegeben werden.
    return Utils.shuffle(recommendations);
  }

  /** gets all ItemGroups, without filter for ordertype */
  public GetAllItemGroups(): Models.SOTItemGroupDataModel2[] {
    return this._sotItemGroups || [];
  }

  /** gets ItemGroups for display with products filtered by schedule */
  public GetItemGroups(): Models.SOTItemGroupViewModel[] {
    const me = this;
    let itemgroups: Models.SOTItemGroupViewModel[] = [];
    // for service check
    if (me._orderType === orderTypes.inHouse) {
      itemgroups = me._sotInHouseItemGroups.map((itemgroup) => new Models.SOTItemGroupViewModel(itemgroup.model, "in_house", me._sotInHouseProducts, me._recipeLinkings, me._recipeLinkingsHideProductIDs, me._productTagFilter, me.product_sort));
    } else if (me._orderType === orderTypes.pickup) {
      itemgroups = me._sotPickupItemGroups.map((itemgroup) => new Models.SOTItemGroupViewModel(itemgroup.model, "pickup", me._sotPickupProducts, me._recipeLinkings, me._recipeLinkingsHideProductIDs, me._productTagFilter, me.product_sort));
    } else {
      me._coreLogService.error("SOTService.GetItemGroups", "unknown ordertype", me._orderType);
    }
    // Filter Scheduled
    // ! Achtung _webshopSettingsDataModel muss vorher geladen werden
    const schedule_req = new ScheduledForRequest(me.GetOrderStart(), me.location, me._webshopSettingsDataModel, me._coreLanguageService, me._sotSettings.article_option);

    itemgroups.forEach(itemgroup => {
      itemgroup.products.forEach(product => product.refreshSchedule(schedule_req));
      itemgroup.products = itemgroup.products.filter(product => !product.schedule.hide);
    });
    // Filter empty itemgroups
    itemgroups = itemgroups.filter(itemgroup => itemgroup.products.length > 0);

    // Filter Artikel TAGs
    if (me._productTagFilter && me._productTagFilter.length > 0) {
      itemgroups.forEach(itemgroup => {
        const hasSomeProductTags = (product_tag_ids: number[]): boolean => {
          return me._productTagFilter.every((id => product_tag_ids.indexOf(id) >= 0))
        }
        itemgroup.products = itemgroup.products.filter(product => hasSomeProductTags(product.product_tag_ids));
      });

      // Filter empty itemgroups
      itemgroups = itemgroups.filter(itemgroup => itemgroup.products.length > 0);
      // Leere Warengruppen, ohne Artikeln mit den Artikel TAGs werden nicht angezeigt
    }
    itemgroups.forEach(item_group => {
      item_group.products.sort(me.product_sort);
    });
    return itemgroups;
  }

  public scheduleReqForRecomendationProduct() {
    const me = this;
    const schedule_req = new ScheduledForRequest(me.GetOrderStart(), me.location, me._webshopSettingsDataModel, me._coreLanguageService, me._sotSettings.article_option);
    return schedule_req;
  }

  public GetAllProducts(): Models.SOTProductViewModel[] {
    const me = this;
    if (me._sotPickupProducts === me._sotInHouseProducts) {
      return me._sotPickupProducts;
    } else {
      return [...me._sotPickupProducts, ...me._sotInHouseProducts]
    }
  }

  /** Liste der Aktiven Artikel TAGs (bereits sortiert) */
  public GetProductTags(): Models.SOTProductTagViewModel[] {
    return this._sotProductTags;
  }

  /** Gibt die Artikel TAGs zu den übergebenen IDs zurück */
  public GetProductTagsByIds(product_tag_ids: number[]): Models.SOTProductTagViewModel[] {
    const me = this;
    const productTags: Models.SOTProductTagViewModel[] = [];
    product_tag_ids.forEach(product_tag_id => {
      const productTag = me._sotProductTagsMap[product_tag_id];
      if (productTag) {
        productTags.push(productTag);
      } else {
        // Keine Fehlermeldung, da zu viele das SOT verlangsamen.
        // Es ist möglich das Kunden den Artikeln Tags zuweisen, die Tags aber für die Oberfläche abschalten, dann werden diese nicht gefunden.
        // Darum wird die Fehlermeldung zur Laufzeit nur einmal ausgegeben.
        if (!me._sotMissingProductTagIds.includes(product_tag_id)) {
          me._sotMissingProductTagIds.push(product_tag_id);
          me._coreLogService.error("SOTService.GetProductTagsByIds", "no product_tag found for id", product_tag_id);
        }
      }
    });
    productTags.sort((a, b) => a.sort_no - b.sort_no);
    return productTags;
  }

  private _productTagFilter: number[] = [];
  /** Setzt ein Filter über Artikel TAGs für das SOT. Filter wird nach dem Setzen Aktiviert */
  public SetProductTagFilter(product_tag_ids: number[] = []): void {
    this._productTagFilter = product_tag_ids;
  }

  private _accessabiltyModeone: boolean = false;
  public SetAccessabiltyMode(useModeOne: boolean): void {
    this._accessabiltyModeone = useModeOne;
  }
  /** Aktuell angewandter Artikel TAG Filter */
  public GetProductTagFilter(): number[] {
    return this._productTagFilter;
  }
  /**selected tags are displayed first */
  public displaySelectedTagFirst(a_tagId: number, b_tagId: number) {
    const me = this;
    const selectedTags = (product_tag_id: number): boolean => {
      return me.GetProductTagFilter().includes((product_tag_id))
    }
    if (!selectedTags(a_tagId) && selectedTags(b_tagId)) {
      return 1;
    } else if (selectedTags(a_tagId) && !selectedTags(b_tagId)) {
      return -1;
    }
    return 0;
  }

  /** wird aufgerufen um die neuen */
  public UseNewAvailability(): void {
    if (this._sotSettings.model.use_product_availability) {
      this._articleAvailabilityCountMap = this._newArticleAvailabilityCountMap;
      this._articleAvailabilityBlacklistMap = this._newArticleAvailabilityBlacklistMap;
    }
  }

  // -1 means it is not an availability item
  // substracts the articleAvailabilityMinCount automaticly
  // product_id aus der Kasse und nicht die ID des Artikels!
  public AvailabilityGetProductCount(productID: number): number {
    if (this._articleAvailabilityObsServ) {
      const count = this._articleAvailabilityCountMap[productID];
      if (count != null) {
        return Math.max(count - this._sotSettings.model.min_product_availability, 0);
      }
    }
    return -1;
  }

  // product_id aus der Kasse und nicht die ID des Artikels!
  public AvailabilityIsProductInStock(productID: number): boolean {
    return this.AvailabilityGetProductCount(productID) != 0;
  }

  // product_id aus der Kasse und nicht die ID des Artikels!
  public AvailabilityIsProductBlacklisted(productID: number): boolean {
    if (this._articleAvailabilityObsServ) {
      const blacklisted = this._articleAvailabilityBlacklistMap[productID];
      if (blacklisted != null) {
        return blacklisted;
      }
    }
    return false;
  }

  // product_id ist verfügbar
  public AvailabilityIsProductAvailable(productID: number): boolean {
    if (this._articleAvailabilityObsServ) {
      return this.AvailabilityGetProductCount(productID) != 0 && !this.AvailabilityIsProductBlacklisted(productID);
    } else {
      return true;
    }
  }

  /** Warenkorb für den SOT */
  public GetBasket(): Models.SOTBasketViewModel {
    return this._sotBasket;
  }

  /** 'Artikel' Warenkorb für den SOT */
  public GetNewProductBasket(product: Models.SOTProductViewModel): Models.SOTBasketViewModel {
    const productBasket = new Models.SOTBasketViewModel(this);
    productBasket.addProduct(product, false);
    return productBasket;
  }

  /** auslesen der verfügbarkeiten, die product.service.ts, nur für den SOT Standort */
  private onArticleAvailbilityUpdate() {
    const me = this;
    me._articleAvailabilityObsServ.GetObjectsByFilter((obj) => obj.location == me.location).then(aas => {
      let productMap = {};
      let blacklistMap = {};
      if (aas.length > 0) {
        const aaIDs = aas[0].product_ids.split("|");
        const aaCounts = aas[0].product_ids_count.split("|");
        if (aaIDs.length === aaCounts.length) {
          aaIDs.forEach((id, index) => productMap[id] = parseInt(aaCounts[index], 10));
        } else {
          me._coreLogService.error("SOTService.onArticleAvailbilityUpdate", "aaIDs and aaCounts should be the same length");
        }
        const aaBlacklistIDs = aas[0].product_ids_blacklist.split("|");
        blacklistMap = aaBlacklistIDs.reduce((a, b) => { a[b] = true; return a; }, {});
      } // else no data for SOT location
      if (!_.isEqual(me._newArticleAvailabilityCountMap, productMap) || !_.isEqual(me._newArticleAvailabilityBlacklistMap, blacklistMap)) {
        me._newArticleAvailabilityCountMap = productMap;
        me._newArticleAvailabilityBlacklistMap = blacklistMap;
      } // else no new data
    }).catch(e => {
      me._coreLogService.error("SOTService.onArticleAvailbilityUpdate", "_articleAvailabilityObsServ.GetObjectsByFilter()", e);
    });
  }

  public validateAgeRequirement(product: Models.SOTProductViewModel): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      try {
        let productRequiredAge = 0;
        productRequiredAge = product.model.ask_age;
        const canAddItem = me._confirmedAge >= productRequiredAge;
        if (!canAddItem) {
          this.OpenAgeConfirmDialog(productRequiredAge, (confirmed) => {
            if (confirmed) {
              try {
                me._confirmedAge = productRequiredAge;
              } catch (error) {
                this._coreLogService.error("sotservice.validateAgeRequirement", "SaveAgeConfirm", error);
              }
              resolve(true);
            } else {
              resolve(false);
            }
          });
        } else {
          resolve(true);
        }
      } catch (error) {
        me._coreLogService.error("sotService.validateAgeRequirement", "catch", error);
        reject(error);
      }
    })
  }

  private OpenAgeConfirmDialog(requiredAge: number, callback: (confirmed: boolean) => void) {
    const me = this;
    if (me._openDialogRef) {
      me._openDialogRef.close("cancel");
    }
    const config = {
      sot_service: this,
      requiredAge: requiredAge,
    }
    me._openDialogRef = me._sotDialogService.openAgeDialog(config);
    me._openDialogRef.afterClosed().subscribe((result) => {
      me._openDialogRef = null;
      if (result === "yes") {
        callback(true);
      } else {
        callback(false);
      }
    });
  }

  /** öffnet den dialog, der fragt ob der anwender den Service rufen möchte */
  public OpenServiceDialog(): void {
    const me = this;
    if (me._openDialogRef) {
      me._openDialogRef.close("cancel");
    }
    me._openDialogRef = me._sotDialogService.openServiceCallDialog(me);
    me._openDialogRef.afterClosed().subscribe((result) => {
      me._openDialogRef = null;
      if (result === "yes") {
        me.SOTConnectionSendServiceCall();
      } // else result == "no" > ignore 
    });
  }

  /** öffnet den dialog, der fragt ob der anwender seine bestellung abbrechen möchte
   * @param go_to_startpage beim abbrechen wird zum startscreen umgelteitet, sonst zu der ordertype
   */
  public OpenOrderCancelDialog(go_to_startpage: boolean): void {
    const me = this;
    if (me._openDialogRef) {
      me._openDialogRef.close("cancel");
    }
    me._openDialogRef = me._sotDialogService.openOrderCancelDialog(me);
    me._openDialogRef.afterClosed().subscribe((result) => {
      me._openDialogRef = null;
      if (result === "yes") {
        if (go_to_startpage) {
          me.GoToStartscreen();
        } else {
          me.GoToOrdertype();
        }
      } // else result == "no" > ignore 
    });
  }

  /** öffnet den dialog, der fragt ob der anwender seine bestellung abbrechen möchte
   */
  public OpenWrongCardDialog(): void {
    const me = this;
    if (me._openDialogRef) {
      me._openDialogRef.close("cancel");
    }
    me._openDialogRef = me._sotDialogService.openWrongCardDialog(me);
    me._openDialogRef.afterClosed().subscribe((result) => {
      me._openDialogRef = null;
      if (result === "rescan") {
        me.SOTConnectionSendScanCard();
      } else if (result === "continue") {
        me.TryCardPayment();
      }
    });
  }

  //#region ### SOT connection communication ###

  /** reaction on sot connection status changes */
  private BindSOTConnection() {
    const me = this;
    // keeps the SOT connection open
    me._connectionStatusChangedSubscription = me._sotWebSocketService.connectionStatusChanged.subscribe(async (status) => {
      if (status) {
        // connection success
        me._sotWebSocketService.Send("$01:Connection success");
        // send bufferd error logs
        while (me._errorLogBuffer.length > 0) {
          if (me._sotWebSocketService.isConnected) {
            // chronologisch die logs übertragen
            me.SOTConnectionSendLog(me._errorLogBuffer.shift());
          } else {
            break; // breaks while, if it disconnected
          }
        }
      } else {
        // try connect
        await me.WaitOpenSOTConnection();
      }
    });
    me._connectionStatusChangedSubscription = me._sotWebSocketService.messageReceived.subscribe((msg) => {
      me._coreLogService.debug("SOTService.BindSOTConnection", "_sotWebSocketService.messageReceived", msg)
      me.SOTConnectionMessageReceived(msg);
    });
  }

  /** wait and try to open connection, for ever */
  private async WaitOpenSOTConnection() {
    const me = this;
    try {
      me._sotDialogService.showLoaderDialog();
      while (! await me._sotWebSocketService.OpenConnection()) { /* try for ever */ }
      me._sotDialogService.hideLoaderDialog();
    } catch (error) {
      me._coreLogService.error("SOTService.BindSOTConnection", "_sotWebSocketService.OpenConnection", error);
    }
  }

  private SOTConnectionMessageReceived(msg: string) {
    const me = this;
    try {
      // get message code
      if (!Utils.IsNullOrWhiteSpace(msg) && msg.length > 4 && msg[0] == "$" && msg[3] == ":") {
        let msg_code = +(msg.substring(1, 3));
        switch (msg_code) {
          case 3: // payment success example: "$03:paymenttype=1;order_no=SUPERMANN;open_amount=0;benefit=OK;bonus=001;..." ("bonus=" muss letzte sein, da der aufbau sonderzeichen enthält)
            if (!me.hasComunicationStatus(SOTComunicationStatus.wait_payment)) {
              me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:payment_success > comunication_status is not wait_payment", me._comunication_status);
            }
            me.setComunicationStatus(SOTComunicationStatus.wait_reset);
            me._reservedRedemptionIds = []; // POS calls ConfirmRedemption
            let success_paymenttype = 0;
            if (msg.length > 17) {
              success_paymenttype = +(msg.substring(16, 17));
              if (me._payType != success_paymenttype) {
                me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:payment_success > success_paymenttype is not _payType", success_paymenttype, me._payType);
              }
            }
            const startOrderNoIndex = msg.indexOf(";");
            const startOrderNo = msg.indexOf("=", startOrderNoIndex) + 1; // +1 skip the '='
            const openAmountIndex = msg.indexOf(";open_amount=", startOrderNoIndex + 1);
            let startBenefitIndex = -1; // wird nur ermittelt, wenn open_amount vorhanden ist
            let startVoucherIndex = -1; // wird nur ermittelt, wenn open_amount vorhanden ist
            if (startOrderNo > 0) {
              if (openAmountIndex > 0) {
                me._order_no = msg.substring(startOrderNo, openAmountIndex);
              } else {
                me._order_no = msg.substring(startOrderNo);
              }

              if (openAmountIndex > 0) {
                startVoucherIndex = msg.indexOf(";bonus=", openAmountIndex + 1);
                startBenefitIndex = msg.indexOf(";benefit=", openAmountIndex + 1);

                let open_amount = "0";
                const openAmountEndIndex = msg.indexOf(";", openAmountIndex + 1);
                if (openAmountEndIndex > 0) {
                  open_amount = msg.substring(openAmountIndex + ";open_amount=".length, openAmountEndIndex);
                } else {
                  open_amount = msg.substring(openAmountIndex + ";open_amount=".length);
                }
                me._order_open_amount = Utils.moneyRound(+(open_amount) / 100);
              }
            }

            if (startBenefitIndex > 0) {
              let benefit = "OK";
              const benefitEndIndex = msg.indexOf(";", startBenefitIndex + 1);
              if (benefitEndIndex > 0) {
                benefit = msg.substring(startBenefitIndex + ";benefit=".length, benefitEndIndex);
              } else {
                benefit = msg.substring(startBenefitIndex + ";benefit=".length);
              }
              me._redeemBenefitStateFailed = benefit != "OK";
            } // else no benefit information = benefit OK, keep _redeemBenefitStateFailed=false

            if (startVoucherIndex > 0) {
              // Bonusteilname(0 oder 1)§LastPointAction|Bakt1_Name§Bakt1_ID§Bakt1PunkteNeu~Bakt1PunkteAlt§Bakt1_EndDay§Bakt1_BenefitName~Bakt1BenefitName2§Bakt1_BenefitPID~Bakt1_BenefitPID2§Bakt1_LossesID~Bakt1_LossesID2§Bakt1_BenefitPunkte~Bakt1_BenefitPunkte2§Bakt1_BenefitEndDay~Bakt1_BenefitEndDay2|Bakt2_Name§Bakt2PunkteNeu~Bakt2PunkteAlt§Bakt2_EndDay§Bakt2_BenefitName§Bakt2_BenefitPID§Bakt2_LossesID§Bakt2_BenefitPunkte§Bakt2_BenefitEndDay
              const VoucherResponse = msg.substring(startVoucherIndex + ";bonus=".length); // +1 skip the ';'
              me._order_bonus_points = WebVoucherService.parseNewBonusPointsFromBonusPointString(1, VoucherResponse);
              if (!me._order_bonus_points) {
                me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:bonus > fail to parse", VoucherResponse);
                me._order_bonus_points = [];
              }
            }

            if (me._payType === Models.PaymentType.ecTerminal || me._payType === Models.PaymentType.atCard || me._payType === Models.PaymentType.voucherPay) {
              // me._evPaymentDone.next(); ist nicht notwendig, da GoToPaymentConfimation es sofort umgeleitet wird
              me.GoToPaymentConfimation();
            } else /* me._payType === Models.PaymentType.atPOS */ {
              if (me._evPaymentDone.observers.length > 0) {
                me._evPaymentDone.next();
              } else {
                me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:payment_success > _evPaymentDone has no observers");
              }
            }
            me.inactive_dlg_start(); // reactivierung da nun der anwender wieder übernimmt
            break;
          case 4: // bon removed, can reset and go to startpage
            if (msg == "$04:reset") {
              if (!me.hasComunicationStatus(SOTComunicationStatus.wait_reset)) {
                me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:reset > comunication_status is not wait_reset", me._comunication_status);
              }
              me.setComunicationStatus(SOTComunicationStatus.free);
              if (me._evResetAfterPayment.observers.length > 0) {
                me._evResetAfterPayment.next();
              } else {
                me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:reset > _evResetAfterPayment has no observers");
                me.GoToStartscreen(); // fall back
              }
            } else {
              me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg is unknown", msg);
            }
            break;
          case 5: // if payment fail example: "$05:paymenttype=1;fail"
            if (!me.hasComunicationStatus(SOTComunicationStatus.wait_payment)) {
              me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:payment_fail > comunication_status is not wait_payment", me._comunication_status);
            }
            me.setComunicationStatus(SOTComunicationStatus.in_payment);
            let fail_paymenttype = 0;
            if (msg.length > 17) {
              fail_paymenttype = +(msg.substring(16, 17));
              if (me._payType != fail_paymenttype) {
                me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:payment_fail > fail_paymenttype is not _payType", fail_paymenttype, me._payType);
              }
            }
            const allow_pay_at_pos = me._payAtPOS && fail_paymenttype == Models.PaymentType.ecTerminal;
            me.OpenPayFailDialog(allow_pay_at_pos, (result) => {
              if (result == "retry_pay") {
                me.SOTConnectionSendRepeat();
              } else if (result == "pos_pay") {
                me.SOTConnectionSendChangePayment(Models.PaymentType.atPOS);
              } else /*if (result == "cancel")*/ {
                me.SOTConnectionSendCancel(true);
              }
            });
            break;
          case 12: // if card scanned example: "$12:{"cardno":"12345678","amount":1.23,"limit":150}"
            me.userActionDetected(true); // Scan counts as Useraction and auto close still there Dialog
            if (!me.hasComunicationStatus(SOTComunicationStatus.wait_scan_card)) {
              me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:card_info > comunication_status is not wait_scan_card", me._comunication_status);
              return; // break
            }
            me.setComunicationStatus(SOTComunicationStatus.free);
            if (msg === "$12:error_card") {
              // sicherheits halber wird der dialog geschloßen, falls er mehrfach geöffnet wird
              if (me._modalDialogRef) {
                me._modalDialogRef.close();
              }
              me._setCard("no_card");
            } else {
              me._setCard(msg.substring(4));
            }
            break;
          case 14: // if pager scanned example: "$14:123"
            me.userActionDetected(true); // Scan counts as Useraction and auto close still there Dialog
            if (!me.hasComunicationStatus(SOTComunicationStatus.wait_scan_pager)) {
              me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:pager > comunication_status is not wait_scan_pager", me._comunication_status);
              return; // break
            }
            me.setComunicationStatus(SOTComunicationStatus.free);
            if (msg === "$14:error_pager") {
              // sicherheits halber wird der dialog geschloßen, falls er mehrfach geöffnet wird
              if (me._modalDialogRef) {
                me._modalDialogRef.close();
              }
              me._modalDialogRef = me._sotDialogService.openCardCouldNotBeReadDialog(me);
              me._setPager("no_pager");
            } else {
              me._setPager(msg.substring(4));
            }
            break;
          case 16: // if coupon scanned example: "$16:https://www.hypersoft.de/?HYAR=12345" (old version "$16:HYAR=12345")
            me.userActionDetected(true); // Scan counts as Useraction and auto close still there Dialog
            if (!me.hasComunicationStatus(SOTComunicationStatus.wait_scan_coupon)) {
              me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:coupon > comunication_status is not wait_scan_coupon", me._comunication_status);
              return; // break
            }
            me.setComunicationStatus(SOTComunicationStatus.free);
            if (msg === "$16:error_scan") {
              me._scanCoupon("no_coupon");
            } else {
              me._scanCoupon(msg.substring(4));
            }
            break;
          case 20: // if single-use coupon validated example: "$20:{"code":"123456789","success":true,"redemption_id":4711,"coupon_id":88,"article_nr":"9001"}"
            if (me.hasComunicationStatus(SOTComunicationStatus.wait_coupon_validation)) {
              me.setComunicationStatus(SOTComunicationStatus.free);
            } else {
              me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:coupon_validation > comunication_status is not wait_coupon_validation", me._comunication_status);
            }
            me._setCouponValidation(msg.substring(4));
            break;
          case 18: // if voucher scanned example: "$18:XXXXXXXXXXXX=001;12345;0000;500;0(|(Customerinfo)|Bonusinfo)"
            me.userActionDetected(true); // Scan counts as Useraction and auto close still there Dialog
            if (!me.hasComunicationStatus(SOTComunicationStatus.wait_scan_voucher)) {
              me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:voucher > comunication_status is not wait_scan_voucher", me._comunication_status);
              return; // break
            }
            me.setComunicationStatus(SOTComunicationStatus.free);
            if (msg === "$18:error_voucher") {
              me._scanVoucher("no_voucher");
            } else if (msg.length === "$18:12345678901234".length) {
              // wenn 14 Stellen zurückkommen, dann erwartet das POS eine PIN
              me._scanVoucher("no_voucher");
              me.OpenManualVoucherPinInput().then((pin) => {
                me._sotWebSocketService.Send("$17:voucher=" + msg.substring(4) + pin);
              }).catch((error) => {
                me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:voucher > OpenManualVoucherPinInput", error);
                me._scanVoucher("no_voucher");
              });
            } else {
              me._scanVoucher(msg.substring(4));
            }
            break;
          case 91: // if printer status changed example: "$91:printererror" or "$91:printerclear"
            if (msg == "$91:printererror") {
              if (me.hasComunicationError()) {
                me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:printererror > comunication_status has error", me._comunication_status);
                me.removeComunicationError();
              }
              me.setComunicationError(SOTComunicationStatus.printer_error);
              // sicherheits halber wird der dialog geschloßen, falls er mehrfach geöffnet wird
              if (me._modalDialogRef) {
                me._modalDialogRef.close();
              }
              me._modalDialogRef = me._sotDialogService.openPrinterErrorDialog(me);
            } else if (msg == "$91:printerclear") {
              if (!me.hasComunicationError(SOTComunicationStatus.printer_error)) {
                me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg:printerclear > comunication_status is not printer_error", me._comunication_status);
              }
              me.removeComunicationError(SOTComunicationStatus.printer_error);
              // sicherheits halber wird der dialog geprüft, falls er mehrfach geschloßen wird
              if (me._modalDialogRef) {
                me._modalDialogRef.close();
                me._modalDialogRef = null;
              }
            } else {
              me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg is unknown", msg);
            }
            break;
          default:
            me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg_code is unknown", msg);
            break;
        }
      } else {
        me._coreLogService.error("SOTService.SOTConnectionMessageReceived", "msg is unknown", msg);
      }
    } catch (error) {
      me._coreLogService.error("SOTService.BindSOTConnection", "_sotWebSocketService.OpenConnection", error);
    }
  }

  /** zahlung wiedeholen, wenn das bezahlen fehlgeschlagen ist */
  public SOTConnectionSendBasket(): void {
    const me = this;
    try {
      if (me.hasComunicationError()) {
        me._coreLogService.error("SOTService.SOTConnectionSendBasket", "hasComunicationError", me._comunication_status);
        return; // break
      }
      if (!me.hasComunicationStatus(SOTComunicationStatus.free)) {
        me._coreLogService.error("SOTService.SOTConnectionSendBasket", "comunication_status is not free", me._comunication_status);
      }
      const basket = me._sotBasket.cretaeCopyAsSOTBasketModel(me._payType, me._card_no, me._pager_no);
      // Trinkgeld berechnung
      if (me._tip_percent > 0) {
        basket.tip = Utils.moneyRound(basket.price * (me._tip_percent / 100.0));
        basket.price = Utils.moneyRound(basket.price + basket.tip);
      }
      // Webgutschein in die Zahlung einbeziehen
      if (me.voucher) {
        basket.webvoucherno = me.voucher.Code;
        if (me.voucherSelectedForPay) {
          basket.partial = true;
          basket.remaining_amount = basket.price;
          const webvoucher_total_amount = Utils.moneyRound(me.voucher.InviteAmount + me.voucher.Amount);
          let webvoucher_pay_amount = Math.min(basket.remaining_amount, webvoucher_total_amount);
          basket.webvouchers.push({
            no: me.voucher.Code,
            amount: webvoucher_pay_amount
          })
          basket.remaining_amount = Utils.moneyRound(basket.remaining_amount - webvoucher_pay_amount);
        }
      }
      me.setComunicationStatus(SOTComunicationStatus.wait_payment);
      me._sotWebSocketService.Send("$02:" + JSON.stringify(basket));
    } catch (error) {
      me._coreLogService.error("SOTService.SOTConnectionSendBasket", "try catch", error);
    }
  }

  /** zahlung wiedeholen, wenn das bezahlen fehlgeschlagen ist */
  public SOTConnectionSendRepeat(): void {
    const me = this;
    try {
      if (me.hasComunicationError()) {
        me._coreLogService.error("SOTService.SOTConnectionSendRepeat", "hasComunicationError", me._comunication_status);
        return; // break
      }
      if (!me.hasComunicationStatus(SOTComunicationStatus.in_payment)) {
        me._coreLogService.error("SOTService.SOTConnectionSendRepeat", "comunication_status is not in_payment", me._comunication_status);
      }
      me.setComunicationStatus(SOTComunicationStatus.wait_payment);
      me._sotWebSocketService.Send("$06:repeat");
    } catch (error) {
      me._coreLogService.error("SOTService.SOTConnectionSendRepeat", "try catch", error);
    }
  }

  /** ander zahlungsart verwenden, wenn das bezahlen fehlgeschlagen ist */
  public SOTConnectionSendChangePayment(paymenttype: Models.PaymentType): void {
    const me = this;
    try {
      if (me.hasComunicationError()) {
        me._coreLogService.error("SOTService.SOTConnectionSendChangePayment", "hasComunicationError", me._comunication_status);
        return; // break
      }
      if (!me.hasComunicationStatus(SOTComunicationStatus.in_payment)) {
        me._coreLogService.error("SOTService.SOTConnectionSendChangePayment", "comunication_status is not in_payment", me._comunication_status);
      }
      me.setComunicationStatus(SOTComunicationStatus.wait_payment);
      me._payType = paymenttype;
      me._sotWebSocketService.Send("$07:paymenttype=" + me._payType + ";repeat");
      if (me._evPaymentChanged.observers.length > 0) {
        me._evPaymentChanged.next();
      } else {
        me._coreLogService.error("SOTService.SOTConnectionSendChangePayment", "_evPaymentChanged has no observers");
      }
    } catch (error) {
      me._coreLogService.error("SOTService.SOTConnectionSendChangePayment", "try catch", error);
    }
  }

  /** abbrechen, wenn das bezahlen fehlgeschlagen ist */
  public SOTConnectionSendCancel(go_to_startpage: boolean): void {
    const me = this;
    try {
      if (me.hasComunicationError()) {
        me._coreLogService.error("SOTService.SOTConnectionSendCancel", "hasComunicationError", me._comunication_status);
        return; // break
      }
      if (!me.hasComunicationStatus(SOTComunicationStatus.in_payment)) {
        me._coreLogService.error("SOTService.SOTConnectionSendCancel", "comunication_status is not in_payment", me._comunication_status);
      }
      me.setComunicationStatus(SOTComunicationStatus.free);
      me._sotWebSocketService.Send("$08:cancel");
      if (go_to_startpage) {
        me.GoToStartscreen();
      }
    } catch (error) {
      me._coreLogService.error("SOTService.SOTConnectionSendCancel", "try catch", error);
    }
  }

  /** abbrechen aller aktiven Lesefunktionen */
  public SOTConnectionSendCancelScanning(): void {
    const me = this;
    try {
      if (me.hasComunicationError()) {
        me._coreLogService.error("SOTService.SOTConnectionSendCancelScanning", "hasComunicationError", me._comunication_status);
        return; // break
      }
      // guest leaves the scan page while the POS validates a scanned coupon: the scanner is already stopped
      if (me.hasComunicationStatus(SOTComunicationStatus.wait_coupon_validation)) {
        me.SOTConnectionInterruptCouponValidation();
        return; // break
      }
      if (!(me.hasComunicationStatus(SOTComunicationStatus.wait_scan_card) || me.hasComunicationStatus(SOTComunicationStatus.wait_scan_pager) || me.hasComunicationStatus(SOTComunicationStatus.wait_scan_coupon) || me.hasComunicationStatus(SOTComunicationStatus.wait_scan_voucher))) {
        me._coreLogService.error("SOTService.SOTConnectionSendCancelScanning", "comunication_status is not waiting for card, pager, coupon or voucher", me._comunication_status);
      }
      me.setComunicationStatus(SOTComunicationStatus.free);
      me._sotWebSocketService.Send("$80:cancel");
    } catch (error) {
      me._coreLogService.error("SOTService.SOTConnectionSendCancelScanning", "try catch", error);
    }
  }

  /** sendet eine log nachricht an den server, damit er diese persistent speichert */
  public SOTConnectionSendLog(msg: string): void {
    const me = this;
    try {
      me._sotWebSocketService.Send("$09:" + msg);
    } catch (error) {
      me._coreLogService.error("SOTService.SOTConnectionSendLog", "try catch", error);
    }
  }

  /** sendet eine service ruf an das POS */
  public SOTConnectionSendServiceCall(): void {
    const me = this;
    try {
      me._sotWebSocketService.Send("$10:service");
    } catch (error) {
      me._coreLogService.error("SOTService.SOTConnectionSendServiceCall", "try catch", error);
    }
  }

  /** Hilfs Funktion zum setzen der Daten zur Vorgangskarte */
  private _setCard(data: string): void {
    const me = this;
    if (data == "no_card") {
      me._card_no = "";
      me._card_amount = 0;
      me._card_limit = 0;
    } else {
      try {
        const card_info = JSON.parse(data);
        // Kartennummer muss aus Zahlen bestehen und nicht 0 sein
        const card_no = +(card_info.cardno);
        if (card_no > 0 && (/^[0-9]*$/g).test(card_info.cardno)) {
          me._card_no = card_info.cardno;
          me._card_amount = +(card_info.amount);
          me._card_limit = +(card_info.limit);
        } else {
          me._coreLogService.error("SOTService._setCard", "data card_no is wrong", data);
          // reset
          me._card_no = "";
          me._card_amount = 0;
          me._card_limit = 0;
        }
      } catch (error) {
        me._coreLogService.error("SOTService._setCard", "catch parse data", data, error);
        // reset
        me._card_no = "";
        me._card_amount = 0;
        me._card_limit = 0;
      }
    }

    if (me._evScanCardDone.observers.length > 0) {
      me._evScanCardDone.next(me._card_no != "");
    } else {
      me._coreLogService.error("SOTService._setCard", "_evScanCardDone has no observers");
    }
  }

  /** erwartet ein scan der Vorgangskarte durch das POS */
  public SOTConnectionSendScanCard(): void {
    const me = this;
    try {
      if (!me.hasComunicationStatus(SOTComunicationStatus.free)) {
        me._coreLogService.error("SOTService.SOTConnectionSendScanCard", "comunication_status is not free", me._comunication_status);
        return; // break
      }
      me.setComunicationStatus(SOTComunicationStatus.wait_scan_card);
      // sende anfrage ans POS
      me._sotWebSocketService.Send("$11:card");
    } catch (error) {
      me._coreLogService.error("SOTService.SOTConnectionSendScanCard", "try catch", error);
    }
  }

  /** Hilfs Funktion zum setzen der Pagernummer */
  private _setPager(data: string): void {
    const me = this;
    if (data == "no_pager") {
      me._pager_no = 0;
    } else {
      try {
        me._pager_no = +(data);
      } catch (error) {
        me._coreLogService.error("SOTService._setPager", "catch parse data", data, error);
        // reset
        me._pager_no = 0;
      }
    }

    if (me._evScanPagerDone.observers.length > 0) {
      me._evScanPagerDone.next(me._pager_no != 0);
    } else {
      me._coreLogService.error("SOTService._setPager", "_evScanPagerDone has no observers");
    }
  }

  /** erwartet ein scan eines Pagers durch das POS */
  public SOTConnectionSendScanPager(): void {
    const me = this;
    try {
      if (!me.hasComunicationStatus(SOTComunicationStatus.free)) {
        me._coreLogService.error("SOTService.SOTConnectionSendScanPager", "comunication_status is not free", me._comunication_status);
        return; // break
      }
      me.setComunicationStatus(SOTComunicationStatus.wait_scan_pager);
      // sende anfrage ans POS
      me._sotWebSocketService.Send("$13:pager");
    } catch (error) {
      me._coreLogService.error("SOTService.SOTConnectionSendScanPager", "try catch", error);
    }
  }

  /** Parses the POS scanner data frame; HYUAR (single-use redemption code) takes priority over the legacy HYAR (plain article) field. */
  private _scanCoupon(data: string): void {
    const me = this;
    let couponCode: string = "none";
    if (data == "no_coupon") {
      couponCode = "0";
    } else {
      const singleUseField = HYUAR_REDEMPTION_CODE_PREFIX; // QR field: single-use redemption code
      const articleNumberField = "HYAR"; // QR field: plain article / legacy coupon code
      try {
        const url = new URL(data.trim());
        const singleUseValue = url.searchParams.get(singleUseField);
        const articleNumber = url.searchParams.get(articleNumberField);
        couponCode = singleUseValue || articleNumber || "";
      } catch (error) {
        if (data.startsWith(`${singleUseField}=`)) {
          couponCode = data.substring(singleUseField.length + 1);
        } else if (data.startsWith(`${articleNumberField}=`)) {
          couponCode = data.substring(articleNumberField.length + 1);
        } else {
          me._coreLogService.error("SOTService._scanCoupon", "catch parse data, length:", data.length, (error && error.name) || "UnknownError");
        }
      }
    }

    if (me._evScanCouponDone.observers.length > 0) {
      me._evScanCouponDone.next(couponCode);
    } else {
      me._coreLogService.error("SOTService._scanCoupon", "_evScanCouponDone has no observers");
    }
  }

  /** erwartet ein scan eines Coupons durch das POS */
  public SOTConnectionSendScanCoupon(): void {
    const me = this;
    try {
      if (!me.hasComunicationStatus(SOTComunicationStatus.free)) {
        me._coreLogService.error("SOTService.SOTConnectionSendScanCoupon", "comunication_status is not free", me._comunication_status);
        return; // break
      }
      me.setComunicationStatus(SOTComunicationStatus.wait_scan_coupon);
      // sende anfrage ans POS
      me._sotWebSocketService.Send("$15:raw_scan");
    } catch (error) {
      me._coreLogService.error("SOTService.SOTConnectionSendScanCoupon", "try catch", error);
    }
  }

  /** helper to set the validation result of a single-use coupon ($20)
   * @param data format {"code":"123456789","success":true,"redemption_id":4711,"coupon_id":88,"article_nr":"9001"}
   */
  private _setCouponValidation(data: string): void {
    const me = this;
    let validation: ISOTCouponValidation = { success: false, error: "InvalidResponse" };
    // an unreadable response counts as the answer to the pending request
    let responseCode = me._couponValidationCode;
    try {
      const response = JSON.parse(data);
      responseCode = response.code != null ? String(response.code) : "";
      if (response.success === true && response.redemption_id != null && response.coupon_id != null && response.article_nr != null) {
        validation = {
          success: true,
          redemption_id: String(response.redemption_id),
          coupon_id: String(response.coupon_id),
          article_nr: String(response.article_nr),
        };
      } else if (response.success === true) {
        me._coreLogService.error("SOTService._setCouponValidation", "success without required fields");
        if (response.redemption_id != null) {
          me._sendReleaseCoupon(String(response.redemption_id));
        }
      } else {
        validation = { success: false, error: response.error || "NotAvailable" };
      }
    } catch (error) {
      me._coreLogService.error("SOTService._setCouponValidation", "catch parse data, length:", data.length);
    }

    if (me._couponValidationCode === "" || responseCode !== me._couponValidationCode) {
      // no pending request (e.g. after a reset): the code is consumed, the SOT no longer uses it
      me._coreLogService.error("SOTService._setCouponValidation", "response without pending request");
      if (validation.success) {
        me._sendReleaseCoupon(validation.redemption_id);
      }
      return;
    }
    me._couponValidationCode = "";
    if (validation.success) {
      me._reservedRedemptionIds.push(validation.redemption_id);
    }

    if (me._evCouponValidationDone.observers.length > 0) {
      me._evCouponValidationDone.next(validation);
    } else {
      me._coreLogService.error("SOTService._setCouponValidation", "_evCouponValidationDone has no observers");
      if (validation.success) {
        me.SOTConnectionSendReleaseCoupon(validation.redemption_id);
      }
    }
  }

  /** lets the POS validate a single-use coupon ($19) and waits for the result ($20)
   * ! no retry: the call at the POS consumes the code
   */
  public SOTConnectionSendValidateCoupon(couponCode: string): Promise<ISOTCouponValidation> {
    const me = this;
    return new Promise<ISOTCouponValidation>((resolve) => {
      try {
        if (!me._sotWebSocketService.isConnected) {
          // no offline mode
          me._coreLogService.error("SOTService.SOTConnectionSendValidateCoupon", "not connected");
          resolve({ success: false, error: "Offline" });
          return;
        }
        if (!me.hasComunicationStatus(SOTComunicationStatus.free)) {
          me._coreLogService.error("SOTService.SOTConnectionSendValidateCoupon", "comunication_status is not free", me._comunication_status);
          resolve({ success: false, error: "Busy" });
          return;
        }
        // no timeout, like the other POS waits: only $20 or a reset via the inactivity dialog ends the wait
        const subscription = me._evCouponValidationDone.pipe(take(1)).subscribe((validation) => {
          me._interruptCouponValidation = null;
          resolve(validation);
        });
        me._interruptCouponValidation = () => {
          subscription.unsubscribe();
          me._interruptCouponValidation = null;
          resolve({ success: false, error: "Interrupted" });
        };
        me._couponValidationCode = couponCode;
        me.setComunicationStatus(SOTComunicationStatus.wait_coupon_validation);
        // send request to the POS
        me._sotWebSocketService.Send("$19:" + JSON.stringify({ code: couponCode }));
      } catch (error) {
        me._coreLogService.error("SOTService.SOTConnectionSendValidateCoupon", "try catch", error);
        resolve({ success: false, error: "Error" });
      }
    });
  }

  /** stops waiting for $20 (reset by the inactivity dialog or guest leaves the scan page)
   * a late $20 then counts as unrequested and a success is released via $21
   */
  private SOTConnectionInterruptCouponValidation(): void {
    const me = this;
    me._couponValidationCode = "";
    if (me.hasComunicationStatus(SOTComunicationStatus.wait_coupon_validation)) {
      me.setComunicationStatus(SOTComunicationStatus.free);
    }
    if (me._interruptCouponValidation) {
      me._interruptCouponValidation();
    }
  }

  /** releases a reserved redemption at the POS ($21), e.g. when the coupon line is removed */
  public SOTConnectionSendReleaseCoupon(redemptionId: string): void {
    const me = this;
    const index = me._reservedRedemptionIds.indexOf(redemptionId);
    if (!redemptionId || index === -1) {
      return;
    }
    me._reservedRedemptionIds.splice(index, 1);
    me._sendReleaseCoupon(redemptionId);
  }

  /** releases all reserved redemptions of the current order (abort / timeout reset) */
  private _releaseAllReservedCoupons(): void {
    const me = this;
    if (me._reservedRedemptionIds.length > 0) {
      me._coreLogService.info("SOTService._releaseAllReservedCoupons", "release coupons: order reset (abort / timeout)", "redemption_ids: " + me._reservedRedemptionIds.join(","));
    }
    me._reservedRedemptionIds.slice().forEach((redemptionId) => me.SOTConnectionSendReleaseCoupon(redemptionId));
  }

  private _sendReleaseCoupon(redemptionId: string): void {
    const me = this;
    try {
      const numericId = +redemptionId;
      me._sotWebSocketService.Send("$21:" + JSON.stringify({ redemption_id: isNaN(numericId) ? redemptionId : numericId }));
    } catch (error) {
      me._coreLogService.error("SOTService._sendReleaseCoupon", "try catch", error);
    }
  }

  /** Hilfs Funktion einlesen eines Voucher
   * @param data aufbau "XXXXXXXXXXXX=001;Wert;0000;Einladung;Rabatt(|(Customerinfo)|Bonusinfo)" Beispiel "001;12345;0000;500;0"
   */
  private _scanVoucher(data: string): void {
    const me = this;
    // vorherigen voucher zurücksetzen
    me._voucher = null;
    try {
      me._voucher = new SOTVoucher(data);
    } catch (error) {
      me._coreLogService.error("SOTService._scanVoucher", "error", error);
    }
    if (me._voucher) {
      me._setUserBonusActionPoints();
    }

    if (me._evScanVoucherDone.observers.length > 0) {
      me._evScanVoucherDone.next(me._voucher);
    } else {
      me._coreLogService.error("SOTService._scanVoucher", "_evScanVoucherDone has no observers");
    }
  }

  /** erwartet ein scan eines Gutscheins durch das POS
   * @param vouchercode ohne Code wird der Scanner gestartet, hat der Gast eine Nummer eingegeben dann wird diese geprüft
   */
  public SOTConnectionSendScanVoucher(vouchercode: string): void {
    const me = this;
    try {
      if (!me.hasComunicationStatus(SOTComunicationStatus.free)) {
        me._coreLogService.error("SOTService.SOTConnectionSendScanVoucher", "comunication_status is not free", me._comunication_status);
        return; // break
      }
      me.setComunicationStatus(SOTComunicationStatus.wait_scan_voucher);
      // sende anfrage ans POS
      if (vouchercode && vouchercode.length > 0) {
        me._sotWebSocketService.Send("$17:voucher=" + vouchercode.toString());
      } else {
        me._sotWebSocketService.Send("$17:voucher");
      }
    } catch (error) {
      me._coreLogService.error("SOTService.SOTConnectionSendScanVoucher", "try catch", error);
    }
  }

  /** buffert die Errorlogs, wenn keine Verbindung zum SOT Server besteht */
  private _errorLogBuffer = new Array<string>();
  private sendErrorLogToSOTServer(msg: string) {
    const me = this;
    if (me._sotWebSocketService.isConnected) {
      me.SOTConnectionSendLog(msg);
    } else {
      me._errorLogBuffer.push(msg);
      if (me._errorLogBuffer.length > MAX_ERROR_BUFFER_LENGTH) {
        me._errorLogBuffer.shift(); // remove oldest log
      }
    }
  }
  private readonly initialColors: { [key: string]: string } = {};
  private readonly initialFontSizes: { [key: string]: string } = {};
  private readonly initialFontFamilies: { [key: string]: string } = {};

  public getInitialFontFamily(font: string): string {
    return this.initialFontFamilies[font];
  }

  public storeInitialColorsAndFontSizes(): void {
    if (Object.keys(this.initialColors).length === 0) {
      const colors = ['--color1', '--color2', '--color3', '--color4', '--color5', '--color6', '--color7'];
      colors.forEach((color, index) => {
        this.initialColors[`--icolor${index + 1}`] = getComputedStyle(document.documentElement).getPropertyValue(color).trim();
      });
    }

    if (Object.keys(this.initialFontSizes).length === 0) {
      const fontSizes = ['--fontsize-main', '--fontsize-display', '--fontsize-large'];
      fontSizes.forEach((fontSize) => {
        this.initialFontSizes[fontSize] = getComputedStyle(document.documentElement).getPropertyValue(fontSize).trim();
      });
    }

    if (Object.keys(this.initialFontFamilies).length === 0) {
      const fontFamilies = ['--fontmain', '--fontdisplay', '--fontextra', '--fontheadline'];
      fontFamilies.forEach((font) => {
        this.initialFontFamilies[font] = getComputedStyle(document.documentElement).getPropertyValue(font).trim();
      });
    }
  }

  public getInitialColors(): { [key: string]: string } {
    return this.initialColors;
  }

  public getInitialFontSizes(): { [key: string]: string } {
    return this.initialFontSizes;
  }

  public updateCssVariables(isModeTwo: boolean): void {
    const colors = ['--color1', '--color2', '--color3', '--color4', '--color5', '--color6', '--color7'];
    const fontSizes = ['--fontsize-main', '--fontsize-display', '--fontsize-large'];
    const fontFamilies = ['--fontmain', '--fontdisplay', '--fontextra', '--fontheadline'];

    if (isModeTwo) {
      colors.forEach((color, index) => {
        document.documentElement.style.setProperty(color, `var(--acessabilty-color${index + 1})`);
      });
      fontSizes.forEach(size => {
        document.documentElement.style.setProperty(size, getComputedStyle(document.documentElement).getPropertyValue(`--acessabilty-fontsize-${size.replace('--fontsize-', '')}`));
      });

      fontFamilies.forEach(font => {
        document.documentElement.style.setProperty(font, getComputedStyle(document.documentElement).getPropertyValue(`--acessabilty-font-family-${font.replace('--font', '')}`));
      });
    } else {
      const initialColors = this.getInitialColors();
      colors.forEach((color, index) => {
        document.documentElement.style.setProperty(color, initialColors[`--icolor${index + 1}`]);
      });

      const initialFontSizes = this.getInitialFontSizes();
      fontSizes.forEach((fontSize) => {
        document.documentElement.style.setProperty(fontSize, initialFontSizes[fontSize]);
      });

      // Reset the font families to the initial values
      fontFamilies.forEach(font => {
        document.documentElement.style.setProperty(font, this.getInitialFontFamily(font));
      });

      this._resetUseModeTwo();
    }
  }
  //#endregion ### END: SOT connection communication ###


  //#region ### Abfragen als Menu ###


  /** Is the menu concept active for view, so that extra is shown as menu */
  public isMenuConceptViewActive(): boolean {
    // momentan nur für frittenwerk, handmadepizzeria und demo
    return this._menu_concept && (env.HypCusNumber == 26045 || env.HypCusNumber == 22021 || env.HypCusNumber == 34007);
  }

  //#endregion ### END: Abfragen als Menu ###
}
